import React, { useEffect, useState, memo, useMemo, useCallback } from "react";
import {
  writeBatch,
  addDoc,
  doc,
  collection,
  getDocs,
  getFirestore,
} from "firebase/firestore";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Helmet } from "react-helmet";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { Spinner } from "../components";
import {
  BarChart3,
  Clock,
  Users,
  ChevronRight,
  Activity,
  X,
  UserCheck,
  Scale,
  Timer,
  Calculator,
  PhoneCall,
  LogOut,
  Briefcase,
  Stethoscope,
  Pill,
} from "lucide-react";
import { useAuth } from "../hooks/AuthenticationProvider";
import {
  medications as MEDICATIONS,
  findMedicationSuggestions,
} from "../utils/medicationList";

const MASTER_MEDS = MEDICATIONS;

/* ───── safe, skip-undefined version ───── */
const renameMedicationForAll = async (orgId, oldName, newName) => {
  const db = getFirestore();
  const batch = writeBatch(db);

  for (const sub of ["residents", "alumni"]) {
    const snap = await getDocs(collection(db, `organizations/${orgId}/${sub}`));

    snap.forEach((docSnap) => {
      const data = docSnap.data();
      const medications = data.medications; // array | object | undefined
      if (!medications) return; // nothing to touch

      let updated;

      if (Array.isArray(medications)) {
        updated = medications.map((m) =>
          m?.name === oldName ? { ...m, name: newName } : m
        );
      } else if (typeof medications === "object") {
        updated = Object.fromEntries(
          Object.entries(medications).map(([k, v]) => [
            k,
            v?.name === oldName ? { ...v, name: newName } : v,
          ])
        );
      }

      // If nothing changed, don't write
      if (JSON.stringify(updated) === JSON.stringify(medications)) return;

      batch.update(doc(db, docSnap.ref.path), { medications: updated });
    });
  }

  await batch.commit();
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length > 0) {
    const value = payload[0].value;
    const percentage = payload[0].payload.percentage;
    const residents = payload[0].payload.residents || [];
    const medications = payload[0].payload.medications || [];
    const isOther = payload[0].name === "Other";

    // Determine what type of data we're dealing with
    const hasMedications = medications.length > 0;
    const hasResidents = residents.length > 0;

    return (
      <div
        className="bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-4 text-slate-300"
        style={{
          position: isOther ? "relative" : "absolute",
          zIndex: 9999,
          marginTop: isOther ? "-100%" : "0",
          marginBottom: isOther ? "1rem" : "0",
          minWidth: isOther ? "200px" : "180px",
          padding: isOther ? "1rem" : "0.75rem",
        }}
      >
        <p className="text-purple-400 font-semibold mb-2 text-base">
          {payload[0].name}
        </p>
        <div className="space-y-2">
          <p className="text-slate-300 flex justify-between">
            <span className="font-medium text-purple-400">Count:</span>
            <span className="text-slate-300">{value}</span>
          </p>
          <p className="text-slate-300 flex justify-between">
            <span className="font-medium text-purple-400">Percentage:</span>
            <span className="text-slate-300">{percentage}%</span>
          </p>

          {/* Show medications list if available */}
          {isOther && hasMedications && (
            <div className="mt-3 pt-3 border-t border-slate-700">
              <p className="text-purple-400 font-medium mb-2">Medications:</p>
              <div className="space-y-1">
                {medications.map((med, index) => (
                  <p key={index} className="text-sm text-slate-300">
                    {med.name}
                    <span className="text-cyan-400 ml-2">({med.count})</span>
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Show residents list if available */}
          {isOther && hasResidents && !hasMedications && (
            <div className="mt-3 pt-3 border-t border-slate-700">
              <p className="text-purple-400 font-medium mb-2">Residents:</p>
              <div className="space-y-1">
                {residents.map((resident, index) => (
                  <p key={index} className="text-sm text-slate-300">
                    {`${resident.firstName} ${resident.lastName}`}
                    {resident.referralLocation && (
                      <span className="text-cyan-400 ml-2">
                        ({resident.referralLocation})
                      </span>
                    )}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};

const MATTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    // Find which series this is from
    const entry = payload[0].payload;

    return (
      <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-4 text-slate-300">
        <p className="text-purple-400 font-semibold mb-2 text-base">
          {entry.name}
        </p>
        <div className="space-y-2">
          <div className="space-y-1">
            <p className="text-slate-300 flex justify-between">
              <span className="font-medium text-red-400">
                Reoccurrence Rate:
              </span>
              <span className="text-slate-300">{entry.reoccurrenceValue}%</span>
            </p>
            <p className="text-slate-300 flex justify-between">
              <span className="font-medium text-green-400">Sobriety Rate:</span>
              <span className="text-slate-300">{entry.soberValue}%</span>
            </p>
          </div>

          <div className="mt-2 pt-2 border-t border-slate-700">
            <p className="text-slate-300 flex justify-between">
              <span className="font-medium text-purple-400">
                Total Residents:
              </span>
              <span className="text-slate-300">{entry.total}</span>
            </p>
            <p className="text-slate-300 flex justify-between">
              <span className="font-medium text-red-400">
                Reoccurrence Count:
              </span>
              <span className="text-slate-300">{entry.value}</span>
            </p>
            <p className="text-slate-300 flex justify-between">
              <span className="font-medium text-green-400">Sober Count:</span>
              <span className="text-slate-300">{entry.soberCount}</span>
            </p>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const CalculationModal = ({ isOpen, onClose, title, content }) => {
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50 p-6 overflow-auto">
        {content}
      </div>
    </Modal>
  );
};

const Modal = ({ isOpen, onClose, children, title }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-center items-center p-4">
      <div className="bg-[#18181B] bg-gradient-to-b from-slate-900/50 to-slate-900/30 backdrop-blur-xl rounded-xl border border-slate-700/50 w-11/12 max-w-7xl max-h-[90vh] overflow-y-auto relative shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between p-6 border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-xl">
          <h2 className="text-2xl font-bold text-white">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800/50 rounded-lg transition-colors"
          >
            <X className="text-slate-400 hover:text-slate-300" size={24} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

/* ─────────── Helper with Save / Cancel ─────────── */
const RenamePanel = ({ oldName, refresh, onClose }) => {
  const { organizationId } = useAuth();
  const [newName, setNewName] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const suggestions = findMedicationSuggestions(oldName).length
    ? findMedicationSuggestions(oldName)
    : MASTER_MEDS; // fallback to full list

  const handleSave = async () => {
    if (!newName) return;
    setSaving(true);
    try {
      await renameMedicationForAll(organizationId, oldName, newName);
      refresh?.(); // re-fetch analytics
    } finally {
      setSaving(false);
      onClose(); // collapse the panel
    }
  };

  return (
    <div className="mt-2 space-y-2">
      {/* dropdown */}
      <select
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        className="w-full bg-slate-800 border border-slate-600 rounded-lg p-2 text-white"
      >
        <option value="">Select correct medication…</option>
        {suggestions.map((s) => {
          const full = `${s.brandName} (${s.genericName})`;
          return (
            <option key={full} value={full}>
              {full}
            </option>
          );
        })}
      </select>

      {/* action row */}
      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={!newName || saving}
          className={`flex-1 px-3 py-2 rounded-lg text-sm ${
            !newName || saving
              ? "bg-slate-700 text-slate-400 cursor-not-allowed"
              : "bg-cyan-600 hover:bg-cyan-700 text-white"
          }`}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          onClick={onClose}
          className="flex-1 px-3 py-2 rounded-lg text-sm bg-slate-700/50 hover:bg-slate-700 text-slate-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const InfoPanel = ({ data, refresh, chartType }) => {
  if (!data) return null;

  const { organizationId } = useAuth(); // grab orgId from context
  const [expandedMedication, setExpandedMedication] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null); // which row is in "rename" mode?

  /* helper to open the single-med rename dropdown */
  const editSingleMed = () => setEditingIndex(0);

  /* shared dropdown renderer */
  const RenameSelect = ({ oldName, onDone }) => {
    const suggestions =
      findMedicationSuggestions(oldName).length > 0
        ? findMedicationSuggestions(oldName)
        : MASTER_MEDS;

    return (
      <select
        autoFocus
        className="mt-2 w-full bg-slate-800 border border-slate-600 rounded-lg p-2 text-white"
        onChange={async (e) => {
          const newName = e.target.value;
          if (!newName) return;
          await renameMedicationForAll(organizationId, oldName, newName);
          if (typeof refresh === "function") refresh(); // ask parent to refetch
          onDone();
        }}
        onBlur={onDone}
      >
        <option value="">Select correct medication…</option>
        {suggestions.map((s) => (
          <option
            key={`${s.brandName}-${s.genericName}`}
            value={`${s.brandName} (${s.genericName})`}
          >
            {s.brandName} ({s.genericName})
          </option>
        ))}
      </select>
    );
  };

  return (
    <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50 p-6 w-full max-h-[600px] overflow-y-auto">
      {/* ───────────── Header ───────────── */}
      <h3 className="text-xl font-bold text-white text-center mb-4">
        {data.name}
      </h3>
      <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
        <p className="text-2xl font-bold text-center text-cyan-400">
          {data.value}
        </p>
        <p className="text-sm text-slate-400 text-center">
          ({data.percentage}%)
        </p>

        {/* ✏️ Rename button for single-med view */}
        {data.name !== "Other" &&
          chartType === "medication" && ( // Only show for medication charts
            <button
              className="mt-3 block mx-auto text-xs text-purple-400 underline hover:text-purple-300"
              onClick={editSingleMed}
            >
              Rename medication
            </button>
          )}
      </div>

      {/* ────────── “Other” bucket ────────── */}
      {data.name === "Other" && Array.isArray(data.medications) && (
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-white text-center">
            Medications
          </h4>

          {[...data.medications]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((med, idx) => (
              <div key={idx}>
                {/* collapsed row */}
                <div
                  className="bg-slate-900/50 p-3 rounded-lg text-slate-300 text-sm
                       hover:bg-slate-900/70 hover:text-white cursor-pointer group"
                  onClick={() =>
                    setExpandedMedication(
                      expandedMedication === idx ? null : idx
                    )
                  }
                >
                  <div className="flex items-center justify-between">
                    <span className="group-hover:text-cyan-400">
                      {med.name}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-cyan-400">({med.count})</span>
                      <ChevronRight
                        className={`w-4 h-4 text-slate-500 group-hover:text-cyan-400 transition-all
                              ${expandedMedication === idx ? "rotate-90" : ""}`}
                      />
                    </div>
                  </div>
                </div>

                {/* expanded section */}
                {expandedMedication === idx && (
                  <div className="mt-2 ml-4 space-y-3">
                    {/* resident list */}
                    {med.residents?.map((r, i) => (
                      <div
                        key={i}
                        className="bg-slate-800/30 p-2 rounded-lg text-slate-400 text-sm"
                      >
                        {r.firstName} {r.lastName}
                      </div>
                    ))}

                    {/* rename UI (only visible while expanded) */}
                    {editingIndex === idx ? (
                      <RenamePanel
                        oldName={med.name}
                        refresh={refresh}
                        onClose={() => setEditingIndex(null)}
                      />
                    ) : (
                      <button
                        className="text-xs text-purple-400 underline hover:text-purple-300"
                        onClick={() => setEditingIndex(idx)}
                      >
                        Rename
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
        </div>
      )}

      {/* ───────────── single medication view ───────────── */}
      {data.name !== "Other" && Array.isArray(data.residents) && (
        <div className="space-y-4">
          {/* dropdown, if user hit “Rename medication” */}
          {editingIndex === 0 && (
            <RenamePanel
              oldName={data.name}
              refresh={refresh}
              onClose={() => setEditingIndex(null)}
            />
          )}

          <h4 className="text-lg font-semibold text-white text-center">
            Residents
          </h4>
          {data.residents.map((r, i) => (
            <div
              key={i}
              className="bg-slate-900/50 p-3 rounded-lg text-slate-300 text-sm"
            >
              {r.firstName} {r.lastName}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ModalChart = memo(
  ({ isOpen, onClose, chartData, chartType, title, refresh }) => {
    const [selectedSlice, setSelectedSlice] = useState(null);

    useEffect(() => {
      if (!isOpen) {
        setSelectedSlice(null);
      }
    }, [isOpen]);

    const handleClick = (data) => {
      setSelectedSlice(data);
    };

    const renderChart = useCallback(() => {
      if (chartType === "pie") {
        return (
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius="80%" // Default radius
              label={({ name, percent }) =>
                `${name} (${(percent * 100).toFixed(1)}%)`
              }
              labelLine={{ strokeWidth: 2, stroke: "#64748b" }}
              onClick={handleClick}
              className="hidden sm:block" // Hide labels on smaller screens
            />
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius="60%" // Smaller radius for mobile
              onClick={handleClick}
              className="block sm:hidden" // Show simplified chart on smaller screens
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.fill || getColorFromPalette(index)}
                />
              ))}
            </Pie>
          </PieChart>
        );
      } else if (chartType === "bar") {
        // Special case for MAT vs Reoccurrence data
        if (title === "MAT vs Reoccurrence") {
          return (
            <BarChart data={chartData}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(148, 163, 184, 0.1)"
              />
              <XAxis
                dataKey="name"
                stroke="#94a3b8"
                tick={{ fill: "#94a3b8" }}
              />
              <YAxis
                stroke="#94a3b8"
                tick={{ fill: "#94a3b8" }}
                tickFormatter={(value) => `${value}%`}
                domain={[0, 100]}
              />
              <Tooltip content={<MATTooltip />} />
              <Legend wrapperStyle={{ color: "#94a3b8" }} />
              <Bar
                name="Reoccurrence Rate"
                dataKey="reoccurrenceValue"
                fill="#FB7185"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                name="Sobriety Rate"
                dataKey="soberValue"
                fill="#4ADE80"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          );
        }

        // Default bar chart for other data
        return (
          <BarChart data={chartData}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(148, 163, 184, 0.1)"
            />
            <XAxis
              dataKey="name"
              stroke="#94a3b8"
              tick={{ fill: "#94a3b8" }}
              interval={0} // This forces all ticks to be shown
              fontSize={10} // Reduce font size if needed to fit all labels
            />
            <YAxis stroke="#94a3b8" />
            <Bar dataKey="value" onClick={handleClick}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.fill || getColorFromPalette(index)}
                />
              ))}
            </Bar>
            <Legend wrapperStyle={{ color: "#94a3b8" }} />
          </BarChart>
        );
      }
      return null;
    }, [chartType, chartData, title, handleClick]);

    return (
      <Modal isOpen={isOpen} onClose={onClose} title={title}>
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="w-full lg:w-3/5 h-96">
            <ResponsiveContainer width="100%" height="100%">
              {renderChart()}
            </ResponsiveContainer>
          </div>
          <div className="w-full lg:w-2/5">
            <InfoPanel
              data={selectedSlice}
              chartType={
                title.toLowerCase().includes("medication")
                  ? "medication"
                  : "other"
              }
              refresh={refresh} // Now refresh is properly passed as a prop
            />
          </div>
        </div>
      </Modal>
    );
  }
);

const RecoveryModal = ({ isOpen, onClose, recoveryData, title }) => {
  if (!isOpen) return null;

  const sortedRecoveryData = recoveryData?.sort((a, b) => {
    const totalDaysA =
      (a.recoveryTime.years || 0) * 365 +
      (a.recoveryTime.months || 0) * 30 +
      (a.recoveryTime.days || 0);
    const totalDaysB =
      (b.recoveryTime.years || 0) * 365 +
      (b.recoveryTime.months || 0) * 30 +
      (b.recoveryTime.days || 0);
    return totalDaysB - totalDaysA;
  });

  const pieChartData = sortedRecoveryData?.map((resident) => {
    const totalDays =
      (resident.recoveryTime.years || 0) * 365 +
      (resident.recoveryTime.months || 0) * 30 +
      (resident.recoveryTime.days || 0);
    const totalRecoveryDays = sortedRecoveryData.reduce((sum, r) => {
      return (
        sum +
        (r.recoveryTime.years || 0) * 365 +
        (r.recoveryTime.months || 0) * 30 +
        (r.recoveryTime.days || 0)
      );
    }, 0);
    const percentage = ((totalDays / totalRecoveryDays) * 100).toFixed(1);
    return {
      name: `${resident.firstName} ${resident.lastName}`,
      value: parseFloat(percentage),
      percentage,
      fill: getRandomColor(),
    };
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-3/5 h-96">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieChartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={150}
                label={({ name, percent }) =>
                  `${name} (${(percent * 100).toFixed(1)}%)`
                }
                labelLine={{ stroke: "#64748b", strokeWidth: 1 }}
              >
                {pieChartData?.map((entry, index) => (
                  <Cell key={index} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="w-full lg:w-2/5 bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50 p-6">
          <div className="space-y-3">
            {sortedRecoveryData?.map((resident, index) => (
              <div
                key={index}
                className="group bg-slate-900/50 p-4 rounded-lg hover:bg-slate-900/70 transform hover:scale-102 transition-all duration-300 cursor-pointer border border-transparent hover:border-slate-700/50"
              >
                <p className="text-lg font-semibold text-white group-hover:text-cyan-400 transition-colors">
                  {`${resident.firstName} ${resident.lastName}`}
                </p>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-400 group-hover:text-slate-300 transition-colors">
                    {`${
                      resident.recoveryTime.years
                        ? resident.recoveryTime.years + "y "
                        : ""
                    }${
                      resident.recoveryTime.months
                        ? resident.recoveryTime.months + "m "
                        : ""
                    }${
                      resident.recoveryTime.days
                        ? resident.recoveryTime.days + "d"
                        : ""
                    }`}
                  </p>
                  <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-cyan-400 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};

// Utility functions
const getRandomColor = () => {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) color += letters[Math.floor(Math.random() * 16)];
  return color;
};

// Add this function before or after your other utility functions (near the getRandomColor function)
const getColorFromPalette = (index) => {
  // Create a palette of colors for consistency
  const colorPalette = [
    "#4ADE80", // Green
    "#FB7185", // Red
    "#60A5FA", // Blue
    "#F59E0B", // Amber
    "#8B5CF6", // Purple
    "#EC4899", // Pink
    "#10B981", // Emerald
    "#6366F1", // Indigo
    "#F43F5E", // Rose
    "#14B8A6", // Teal
    "#8B5CF6", // Violet
    "#EF4444", // Red
    "#22D3EE", // Cyan
    "#A78BFA", // Purple
    "#F97316", // Orange
  ];

  // Use modulo to cycle through colors if we have more data points than colors
  return colorPalette[index % colorPalette.length];
};

// Helper function to format days as "X years Y months Z days"
const formatDaysToMonthsAndDays = (totalDays) => {
  const years = Math.floor(totalDays / 365);
  const remainingDays = totalDays % 365;
  const months = Math.floor(remainingDays / 30);
  const days = Math.round(remainingDays % 30);

  let result = "";

  if (years > 0) {
    result += `${years} year${years > 1 ? "s" : ""} `;
  }

  if (months > 0) {
    result += `${months} month${months > 1 ? "s" : ""} `;
  }

  if (days > 0 || (years === 0 && months === 0)) {
    result += `${days} day${days > 1 ? "s" : ""}`;
  }

  return result.trim();
};

const calculatePercentages = (data) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  return data.map((item) => ({
    ...item,
    percentage: ((item.value / total) * 100).toFixed(1),
    label: `${((item.value / total) * 100).toFixed(1)}%`,
  }));
};

const Analytics = () => {
  const { user, isLoading, organizationId, organizationData, checkPermission } =
    useAuth();
  const navigate = useNavigate();
  const db = getFirestore();
  const housesData = organizationData?.housesData || {};
  const [isCurrent, setIsCurrent] = useState(false);
  const [selectedHouse, setSelectedHouse] = useState("");
  const [viewType, setViewType] = useState("intake");
  const [deduplicateResidents, setDeduplicateResidents] = useState(false);
  const [currentStatusFilter, setCurrentStatusFilter] = useState("discharge");
  const [combinedResidents, setCombinedResidents] = useState([]);
  const [showProbationParoleInfo, setShowProbationParoleInfo] = useState(false);
  const [showRecidivismInfo, setShowRecidivismInfo] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [recidivismFilter, setRecidivismFilter] = useState("all");
  const [medicationViewType, setMedicationViewType] = useState("count");
  const [riskFactorFilter, setRiskFactorFilter] = useState("all");
  const initializeAnalyticsData = (housesData) => ({
    ageDemographics: {},
    medicationData: [],
    matData: [],
    probationParoleData: [],
    substanceOfChoiceData: [],
    matReoccurrenceData: [],
    sobrietyStatusData: [],
    dischargeStatusData: [],
    referralData: [],
    occupiedBeds: Object.keys(housesData || {}).reduce(
      (acc, house) => ({ ...acc, [house]: 0 }),
      {}
    ),
    houseData: Object.keys(housesData || {}).reduce(
      (acc, house) => ({
        ...acc,
        [house]: {
          occupiedBeds: 0,
          sobrietyTime: { years: 0, months: 0, days: 0 },
          lengthOfStay: 0,
          residentCount: 0,
          color: housesData[house]?.color || "#ccc", // Default color
        },
      }),
      {}
    ),
    sobrietyTimes: Object.keys(housesData || {}).reduce(
      (acc, house) => ({ ...acc, [house]: { years: 0, months: 0, days: 0 } }),
      { total: { years: 0, months: 0, days: 0 } }
    ),
    medianRelapseDays: null,
    timeSinceLastRelapse: Object.keys(housesData || {}).reduce(
      (acc, house) => ({ ...acc, [house]: null }),
      {}
    ),
    averageLengthOfStay: Object.keys(housesData || {}).reduce(
      (acc, house) => ({ ...acc, [house]: 0 }),
      { total: 0 }
    ),
    medianLengthOfStay: Object.keys(housesData || {}).reduce(
      (acc, house) => ({ ...acc, [house]: 0 }),
      { total: 0 }
    ),
    averageSoberDaysOnIntake: Object.keys(housesData || {}).reduce(
      (acc, house) => ({ ...acc, [house]: 0 }),
      { total: 0 }
    ),
    medianSoberDaysOnIntake: Object.keys(housesData || {}).reduce(
      (acc, house) => ({ ...acc, [house]: 0 }),
      { total: 0 }
    ),
  });

  const [analyticsData, setAnalyticsData] = useState(() =>
    initializeAnalyticsData(housesData)
  );
  const [soberDaysDisplayType, setSoberDaysDisplayType] = useState("average");
  const [modalChartData, setModalChartData] = useState(null);
  const [modalChartType, setModalChartType] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [recoveryModalOpen, setRecoveryModalOpen] = useState(false);
  const [houseModalData, setHouseModalData] = useState(null);
  const [houseModalTitle, setHouseModalTitle] = useState("");
  const [relapseDisplayType, setRelapseDisplayType] = useState("average");
  const [stayDisplayType, setStayDisplayType] = useState("average");
  const residents = combinedResidents;
  const [calculationModalOpen, setCalculationModalOpen] = useState(false);
  const [calculationTitle, setCalculationTitle] = useState("");
  const [calculationContent, setCalculationContent] = useState(null);

  const fetchAllResidents = async () => {
    setDataLoading(true);
    try {
      // Get current residents from context
      const currentResidents = organizationData?.residents || [];

      // Fetch alumni from Firestore
      const alumniCollectionRef = collection(
        db,
        `organizations/${organizationId}/alumni`
      );
      const alumniSnapshot = await getDocs(alumniCollectionRef);
      const alumniList = alumniSnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
        currentResident: false, // Mark alumni as not current residents
      }));

      // Combine the two sets of data
      const allResidents = [
        ...currentResidents.map((resident) => ({
          ...resident,
          currentResident: true,
        })),
        ...alumniList,
      ];

      setCombinedResidents(allResidents);
    } catch (error) {
      console.error("Error fetching residents and alumni:", error);
    } finally {
      setDataLoading(false);
    }
  };

  // 4. Add useEffect to fetch data when component mounts
  useEffect(() => {
    if (organizationId) {
      fetchAllResidents();
    }
  }, [organizationId, organizationData]);

  // A function to find the program start date from the alumni collection
  const findProgramStartDate = async (organizationId) => {
    try {
      // Reference to the alumni collection
      const alumniCollectionRef = collection(
        db,
        `organizations/${organizationId}/alumni`
      );

      // Get all alumni documents
      const alumniSnapshot = await getDocs(alumniCollectionRef);

      // Extract all valid intake dates
      const intakeDates = alumniSnapshot.docs
        .map((doc) => doc.data().intakeDate)
        .filter((date) => date) // Filter out undefined or null dates
        .sort((a, b) => moment(a).diff(moment(b))); // Sort chronologically

      // Return the earliest date or null if no dates found
      return intakeDates.length > 0 ? intakeDates[0] : null;
    } catch (error) {
      console.error("Error finding program start date:", error);
      return null;
    }
  };

  const getRecoveryGridLayout = () => {
    const numberOfHouses = Object.keys(housesData).length;

    if (numberOfHouses === 1) {
      return "grid-cols-1 max-w-sm mx-auto place-items-center h-full"; // Single centered column
    } else if (numberOfHouses === 2) {
      return "grid-cols-2 max-w-lg mx-auto place-items-center h-full"; // Two columns centered
    } else if (numberOfHouses <= 4) {
      return "grid-cols-2 max-w-xl mx-auto place-items-center h-full"; // Two columns for 3-4 houses
    }
    return "grid-cols-3 max-w-2xl mx-auto place-items-center h-full"; // Three columns for 5+ houses
  };

  const getRecoveryCardHeight = () => {
    const numberOfHouses = Object.keys(housesData).length;
    if (numberOfHouses === 1) {
      return "h-32"; // Taller for single house
    } else if (numberOfHouses <= 2) {
      return "h-28"; // Slightly shorter for 2 houses
    } else if (numberOfHouses <= 4) {
      return "h-24"; // Even shorter for 3-4 houses
    }
    return "h-20"; // Compact for 5+ houses
  };

  const getRecoveryFontSizes = () => {
    const numberOfHouses = Object.keys(housesData).length;
    if (numberOfHouses === 1) {
      return {
        houseName: "text-2xl", // Biggest house name
        sobrietyTime: "text-xl", // Biggest time display
      };
    } else if (numberOfHouses === 2) {
      return {
        houseName: "text-xl",
        sobrietyTime: "text-lg",
      };
    } else if (numberOfHouses <= 4) {
      return {
        houseName: "text-lg",
        sobrietyTime: "text-base",
      };
    }
    return {
      houseName: "text-base",
      sobrietyTime: "text-sm",
    };
  };

  // Open individual house modal
  const openHouseModal = (house) => {
    // Filter function to get current residents dynamically
    const getCurrentResidents = (residents) =>
      residents.filter((resident) => resident.currentResident);

    // Fetch residents data dynamically from `derivedData` or `housesData`
    if (house === "total") {
      // Aggregate all current residents across all houses
      const allCurrentResidents = getCurrentResidents(residents).map(
        (resident) => ({
          firstName: resident.firstName,
          lastName: resident.lastName,
          recoveryTime: calculateLengthOfSobriety(resident.sobrietyDate) || {
            years: 0,
            months: 0,
            days: 0,
          },
        })
      );
      setHouseModalData(allCurrentResidents);
      setHouseModalTitle("All Houses");
    } else {
      // Fetch residents for the specific house dynamically
      const currentResidentsInHouse = getCurrentResidents(residents)
        .filter((resident) => resident.house === house)
        .map((resident) => ({
          firstName: resident.firstName,
          lastName: resident.lastName,
          recoveryTime: calculateLengthOfSobriety(resident.sobrietyDate) || {
            years: 0,
            months: 0,
            days: 0,
          },
        }));
      setHouseModalData(currentResidentsInHouse);
      setHouseModalTitle(house);
    }

    setRecoveryModalOpen(true); // Open the modal
  };

  // Chart opening and closing functions
  const openModalChart = (data, type, title) => {
    setModalChartData(data);
    setModalChartType(type);
    setModalTitle(title);
  };

  const closeModalChart = () => {
    setModalChartData(null);
    setModalChartType(null);
    setModalTitle("");
  };

  // Calculate sober days on intake (days between sobriety date and intake date)
  const calculateSoberDaysOnIntake = (resident, useHouseSpecific = false) => {
    if (!resident.sobrietyDate) return null;

    let relevantIntakeDate;

    // For house-specific calculations
    if (useHouseSpecific) {
      // If resident has house history, use the moveInDate from their current house entry
      if (resident.houseHistory && resident.houseHistory.length > 0) {
        // Find the entry for their current house
        const currentHouseEntry = resident.houseHistory.find(
          (entry) => entry.house === resident.house && !entry.moveOutDate
        );

        // If found, use that moveInDate, otherwise fall back to the specific house moveInDate or the general intakeDate
        if (currentHouseEntry) {
          relevantIntakeDate = currentHouseEntry.moveInDate;
        } else {
          // Try to use house-specific moveInDate (e.g., lyford1MoveInDate)
          const houseSpecificDateField = `${resident.house
            .replace(/[^a-zA-Z0-9]/g, "")
            .toLowerCase()}MoveInDate`;
          relevantIntakeDate =
            resident[houseSpecificDateField] || resident.intakeDate;
        }
      } else {
        // If no house history, check for house-specific moveInDate first
        const houseSpecificDateField = `${resident.house
          .replace(/[^a-zA-Z0-9]/g, "")
          .toLowerCase()}MoveInDate`;
        relevantIntakeDate =
          resident[houseSpecificDateField] || resident.intakeDate;
      }
    } else {
      // For overall program calculation, always use the original intake date
      relevantIntakeDate = resident.intakeDate;
    }

    // If we still don't have a relevant intake date, return null
    if (!relevantIntakeDate) return null;

    const sobrietyDate = moment(resident.sobrietyDate);
    const intakeDate = moment(relevantIntakeDate);

    // Only count positive values (sobriety date before intake date)
    const days = intakeDate.diff(sobrietyDate, "days");
    return days >= 0 ? days : null;
  };

  // Calculate average sober days on intake
  const calculateAverageSoberDaysOnIntake = (
    residents,
    useHouseSpecific = false
  ) => {
    const soberDays = residents
      .map((resident) => calculateSoberDaysOnIntake(resident, useHouseSpecific))
      .filter((days) => days !== null);

    if (soberDays.length === 0) return 0;

    const total = soberDays.reduce((sum, days) => sum + days, 0);
    return total / soberDays.length;
  };

  // Calculate median sober days on intake
  const calculateMedianSoberDaysOnIntake = (
    residents,
    useHouseSpecific = false
  ) => {
    const soberDays = residents
      .map((resident) => calculateSoberDaysOnIntake(resident, useHouseSpecific))
      .filter((days) => days !== null)
      .sort((a, b) => a - b);

    if (soberDays.length === 0) return 0;

    const middle = Math.floor(soberDays.length / 2);
    return soberDays.length % 2 === 0
      ? (soberDays[middle - 1] + soberDays[middle]) / 2
      : soberDays[middle];
  };

  const getMostRecentRelapseDate = (house) => {
    if (!residents || residents.length === 0) return null;

    try {
      // Filter residents based on the house and sobriety status
      const notSoberResidents = residents.filter(
        (resident) =>
          resident.house === house &&
          resident.sobrietyStatus === "Not Sober" &&
          resident.dischargeDate // Ensure dischargeDate exists
      );

      if (notSoberResidents.length === 0) return null;

      // Find the most recent discharge date
      const mostRecentTimestamp = Math.max(
        ...notSoberResidents.map((r) => new Date(r.dischargeDate).getTime())
      );

      // Convert the timestamp back to a Date object
      return new Date(mostRecentTimestamp);
    } catch (error) {
      console.error(
        `Error getting most recent relapse date for house "${house}":`,
        error
      );
      return null;
    }
  };

  // Calculate time since the last relapse
  const calculateTimeSinceLastRelapse = (relapseDate) => {
    if (!relapseDate) return null;
    const duration = moment.duration(moment().diff(moment(relapseDate)));
    return {
      years: duration.years(),
      months: duration.months(),
      days: duration.days(),
    };
  };

  const calculateLengthOfSobriety = (sobrietyDate) => {
    if (!sobrietyDate) return null;
    const start = moment(sobrietyDate, "YYYY-MM-DD").startOf("day");
    const end = moment().startOf("day"); // This is the key change
    if (!start.isValid()) {
      console.warn(`Invalid sobriety date format: ${sobrietyDate}`);
      return null;
    }
    const duration = moment.duration(end.diff(start));
    return {
      years: duration.years(),
      months: duration.months(),
      days: duration.days(),
    };
  };

  // Corrected processMatReoccurrenceData function
  const processMatReoccurrenceData = (residents) => {
    // Filter for residents with valid sobrietyStatus
    const validResidents = residents.filter(
      (resident) =>
        resident.sobrietyStatus === "Sober" ||
        resident.sobrietyStatus === "Not Sober"
    );

    // Separate residents by MAT status
    const onMat = validResidents.filter((resident) => resident.isMAT);
    const notOnMat = validResidents.filter((resident) => !resident.isMAT);

    const result = [];

    // Process "On MAT" stats
    if (onMat.length > 0) {
      const onMatReoccurrenceResidents = onMat.filter(
        (r) => r.sobrietyStatus === "Not Sober"
      );
      const onMatSoberResidents = onMat.filter(
        (r) => r.sobrietyStatus === "Sober"
      );
      const onMatReoccurrenceRate = parseFloat(
        ((onMatReoccurrenceResidents.length / onMat.length) * 100).toFixed(1)
      );
      const onMatSoberRate = parseFloat(
        ((onMatSoberResidents.length / onMat.length) * 100).toFixed(1)
      );

      // Create the "On MAT" data point
      result.push({
        name: "On MAT",
        // For the bar chart visuals
        reoccurrenceValue: onMatReoccurrenceRate,
        soberValue: onMatSoberRate,

        // For the tooltip with reoccurrence data
        value: onMatReoccurrenceResidents.length,
        percentage: onMatReoccurrenceRate,
        residents: onMatReoccurrenceResidents,

        // For the tooltip with sobriety data
        soberCount: onMatSoberResidents.length,
        soberPercentage: onMatSoberRate,
        soberResidents: onMatSoberResidents,

        // Total count for context
        total: onMat.length,
      });
    }

    // Process "Not on MAT" stats
    if (notOnMat.length > 0) {
      const notOnMatReoccurrenceResidents = notOnMat.filter(
        (r) => r.sobrietyStatus === "Not Sober"
      );
      const notOnMatSoberResidents = notOnMat.filter(
        (r) => r.sobrietyStatus === "Sober"
      );
      const notOnMatReoccurrenceRate = parseFloat(
        (
          (notOnMatReoccurrenceResidents.length / notOnMat.length) *
          100
        ).toFixed(1)
      );
      const notOnMatSoberRate = parseFloat(
        ((notOnMatSoberResidents.length / notOnMat.length) * 100).toFixed(1)
      );

      // Create the "Not on MAT" data point
      result.push({
        name: "Not on MAT",
        // For the bar chart visuals
        reoccurrenceValue: notOnMatReoccurrenceRate,
        soberValue: notOnMatSoberRate,

        // For the tooltip with reoccurrence data
        value: notOnMatReoccurrenceResidents.length,
        percentage: notOnMatReoccurrenceRate,
        residents: notOnMatReoccurrenceResidents,

        // For the tooltip with sobriety data
        soberCount: notOnMatSoberResidents.length,
        soberPercentage: notOnMatSoberRate,
        soberResidents: notOnMatSoberResidents,

        // Total count for context
        total: notOnMat.length,
      });
    }

    return result;
  };

  const processRecidivismData = (alumniResidents, filterType = "all") => {
    // Filter for alumni only (not current residents)
    let filteredAlumni = alumniResidents.filter(
      (resident) => !resident.currentResident
    );

    // DEDUPLICATE: Remove duplicates (names with parentheses)
    const deduplicatedAlumni = [];
    const nameGroups = {};

    filteredAlumni.forEach((resident) => {
      if (!resident.fullName) {
        deduplicatedAlumni.push(resident);
        return;
      }

      const match = resident.fullName.match(/^(.+?)\s*\((\d+)\)$/);
      if (match) {
        const baseName = match[1].trim();
        const stayNumber = parseInt(match[2]);

        console.log(`Analytics: Found duplicate: ${resident.fullName}`);

        // Only keep the highest numbered stay for each person
        if (
          !nameGroups[baseName] ||
          nameGroups[baseName].stayNumber < stayNumber
        ) {
          nameGroups[baseName] = { resident, stayNumber };
        }
        // DO NOT add to deduplicatedAlumni here - we'll add the highest stays later
      } else {
        // Check if this is a base name that has duplicates
        const hasNumberedDuplicates = filteredAlumni.some(
          (r) => r.fullName && r.fullName.startsWith(resident.fullName + " (")
        );

        if (!hasNumberedDuplicates) {
          // This person has no duplicates, add them normally
          deduplicatedAlumni.push(resident);
        } else {
          // This is the base version of someone who has duplicates
          // Only add if we haven't seen a numbered version yet
          if (!nameGroups[resident.fullName]) {
            nameGroups[resident.fullName] = { resident, stayNumber: 1 };
          }
        }
      }
    });

    // Add only the highest numbered stays from nameGroups
    Object.values(nameGroups).forEach(({ resident }) => {
      deduplicatedAlumni.push(resident);
    });

    console.log("=== ANALYTICS RECIDIVISM DEDUPLICATION ===");
    console.log("Original alumni count:", filteredAlumni.length);
    console.log("After deduplication:", deduplicatedAlumni.length);
    console.log(
      "Removed duplicates:",
      filteredAlumni.length - deduplicatedAlumni.length
    );

    // Apply probation/parole filter if selected
    let finalFilteredAlumni = deduplicatedAlumni;
    if (filterType === "probationParole") {
      finalFilteredAlumni = deduplicatedAlumni.filter(
        (resident) => resident.poFirstName || resident.poLastName
      );
    }

    // Separate those with and without rearrest dates
    const rearrested = finalFilteredAlumni.filter(
      (resident) => resident.rearrestDate
    );
    const notRearrested = finalFilteredAlumni.filter(
      (resident) => !resident.rearrestDate
    );

    const recidivismData = [
      {
        name: "Re-arrested",
        value: rearrested.length,
        residents: rearrested,
        fill: "#FB7185", // Red
      },
      {
        name: "Not Re-arrested",
        value: notRearrested.length,
        residents: notRearrested,
        fill: "#4ADE80", // Green
      },
    ];

    return calculatePercentages(recidivismData);
  };

  // Process employment data
  const processEmploymentData = (residentsArray, view) => {
    // Determine field to check based on view type
    const fieldToCheck = view === "intake" ? "workOnEntry" : "workStatus";

    // Valid employment statuses (Part-Time and Full-Time are considered employed)
    const employed = [];
    const unemployed = [];

    residentsArray.forEach((resident) => {
      const workStatus = resident[fieldToCheck];

      // Skip retired residents as requested
      if (workStatus === "Retired") {
        return;
      }

      // Count as employed if Full-Time or Part-Time
      if (workStatus === "Full-Time" || workStatus === "Part-Time") {
        employed.push(resident);
      }
      // Count as unemployed if explicitly "Unemployed"
      else if (workStatus === "Unemployed") {
        unemployed.push(resident);
      }
      // Other values are not counted
    });

    // Create data structure for chart
    const employmentData = [
      {
        name: "Employed",
        value: employed.length,
        residents: employed,
        fill: "#4ADE80", // Green
      },
      {
        name: "Unemployed",
        value: unemployed.length,
        residents: unemployed,
        fill: "#FB7185", // Red
      },
    ];

    // Calculate percentages
    return calculatePercentages(employmentData);
  };

  // Calculate employment percentage for UI
  const calculateEmployedPercentage = () => {
    const relevantResidents =
      viewType === "intake"
        ? residents.filter((resident) =>
            isCurrent && !selectedHouse
              ? resident.currentResident
              : isCurrent && selectedHouse
              ? resident.currentResident && resident.house === selectedHouse
              : !isCurrent && selectedHouse
              ? resident.house === selectedHouse
              : true
          )
        : residents.filter(
            (resident) =>
              !resident.currentResident && // Changed from currentResident to !currentResident
              (selectedHouse ? resident.house === selectedHouse : true)
          );

    const employmentData = processEmploymentData(relevantResidents, viewType);
    const total = employmentData.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) return 0;

    const employedItem = employmentData.find(
      (item) => item.name === "Employed"
    );
    return employedItem ? parseFloat(employedItem.percentage) : 0;
  };

  const employedPercentage = calculateEmployedPercentage();

  const saveWeeklySnapshot = async () => {
    try {
      console.log("Starting saveWeeklySnapshot function");

      const {
        ageDemographics,
        medicationData,
        matData,
        probationParoleData,
        sobrietyStatusData,
        dischargeStatusData,
        averageLengthOfStay,
        medianLengthOfStay,
        averageSoberDaysOnIntake,
        medianSoberDaysOnIntake,
        sobrietyTimes,
        houseData, // Add this to get occupiedBeds from houseData
      } = analyticsData;

      // Calculate occupied beds from houseData
      const occupiedBeds = Object.entries(houseData).reduce(
        (acc, [houseName, data]) => {
          acc[houseName] = data.occupiedBeds;
          return acc;
        },
        {}
      );

      // Dynamically calculate total beds and percentage full
      const totalBeds = Object.values(housesData).reduce(
        (sum, house) => sum + house.totalBeds,
        0
      );
      const totalOccupiedBeds = Object.values(occupiedBeds).reduce(
        (sum, count) => sum + count,
        0
      );
      const percentageFull =
        totalBeds > 0 ? Math.round((totalOccupiedBeds / totalBeds) * 100) : 0;

      // Helper function to simplify data structures
      const simplifyData = (data, keys) => {
        if (!Array.isArray(data)) return [];
        return data.map((item) =>
          Object.fromEntries(keys.map((key) => [key, item[key]]))
        );
      };

      // Collect sobriety times data
      const sobrietyTimesData = Object.entries(housesData).reduce(
        (acc, [houseName]) => {
          acc[houseName] = sobrietyTimes[houseName] || {
            years: 0,
            months: 0,
            days: 0,
          };
          return acc;
        },
        {}
      );

      //Employment Status
      const employmentData = {
        intake: {
          employed: combinedResidents.filter(
            (r) =>
              r.workOnEntry === "Full-Time" || r.workOnEntry === "Part-Time"
          ).length,
          unemployed: combinedResidents.filter(
            (r) => r.workOnEntry === "Unemployed"
          ).length,
        },
        discharge: {
          // Changed 'current' to 'discharge'
          employed: combinedResidents.filter(
            (r) =>
              !r.currentResident && // Changed from r.currentResident to !r.currentResident
              (r.workStatus === "Full-Time" || r.workStatus === "Part-Time")
          ).length,
          unemployed: combinedResidents.filter(
            (r) => !r.currentResident && r.workStatus === "Unemployed" // Changed from r.currentResident to !r.currentResident
          ).length,
        },
      };

      // Calculate percentages for both metrics
      const calculatePercentage = (employed, unemployed) => {
        const total = employed + unemployed;
        return total > 0 ? Math.round((employed / total) * 100) : 0;
      };

      const employmentPercentages = {
        intake: calculatePercentage(
          employmentData.intake.employed,
          employmentData.intake.unemployed
        ),
        discharge: calculatePercentage(
          employmentData.discharge.employed,
          employmentData.discharge.unemployed
        ),
      };

      // Calculate total sobriety
      const totalSobriety = Object.entries(sobrietyTimes)
        .filter(([house]) => house !== "total") // Exclude precomputed total
        .reduce(
          (acc, [_, curr]) => {
            if (!curr) return acc;
            acc.years += curr.years || 0;
            acc.months += curr.months || 0;
            acc.days += curr.days || 0;
            if (acc.days >= 30) {
              acc.months += Math.floor(acc.days / 30);
              acc.days %= 30;
            }
            if (acc.months >= 12) {
              acc.years += Math.floor(acc.months / 12);
              acc.months %= 12;
            }
            return acc;
          },
          { years: 0, months: 0, days: 0 }
        );

      // Get relapse data
      const relapseData = await Promise.all(
        Object.keys(housesData).map(async (houseName) => ({
          house: houseName,
          relapse: await calculateTimeSinceLastRelapse(
            await getMostRecentRelapseDate(houseName)
          ),
        }))
      );

      const analyticsSnapshot = {
        timestamp: new Date(),
        organizationId, // Add organization ID to the snapshot
        ageDemographics: Object.fromEntries(
          Object.entries(ageDemographics).map(([key, value]) => [
            key,
            value.count,
          ])
        ),
        employmentData: employmentData,
        employmentPercentages: employmentPercentages,
        medicationData: simplifyData(medicationData, [
          "name",
          "count",
          "value",
        ]),
        matData: simplifyData(matData, ["name", "value", "percentage"]),
        probationParoleData: simplifyData(probationParoleData, [
          "name",
          "value",
          "percentage",
        ]),
        sobrietyStatusData: simplifyData(sobrietyStatusData, [
          "name",
          "value",
          "percentage",
        ]),
        dischargeStatusData: simplifyData(dischargeStatusData, [
          "name",
          "value",
          "percentage",
        ]),
        sobrietyTimes: sobrietyTimesData,
        totalSobriety,
        relapseData: relapseData.reduce((acc, { house, relapse }) => {
          acc[house] = relapse || null;
          return acc;
        }, {}),
        occupiedBeds,
        percentageFull,
        averageLengthOfStay: {
          ...Object.entries(housesData).reduce((acc, [houseName]) => {
            const avg = calculateAverageStay(
              combinedResidents.filter((r) => r.house === houseName),
              houseName
            );
            acc[houseName] = Math.round(avg);
            return acc;
          }, {}),
          total: Math.round(calculateAverageStay(combinedResidents)),
        },
        medianLengthOfStay: {
          ...Object.entries(housesData).reduce((acc, [houseName]) => {
            const median = calculateMedianStay(
              combinedResidents.filter((r) => r.house === houseName),
              houseName
            );
            acc[houseName] = Math.round(median);
            return acc;
          }, {}),
          total: Math.round(calculateMedianStay(combinedResidents)),
        },
        recidivismData: {
          all: processRecidivismData(combinedResidents, "all").map((item) => ({
            name: item.name,
            value: item.value,
            percentage: item.percentage,
          })),
          probationParole: processRecidivismData(
            combinedResidents,
            "probationParole"
          ).map((item) => ({
            name: item.name,
            value: item.value,
            percentage: item.percentage,
          })),
        },
        averageSoberDaysOnIntake: {
          // For total, use the original intake date (useHouseSpecific = false)
          total: Math.round(
            calculateAverageSoberDaysOnIntake(combinedResidents, false)
          ),
          ...Object.keys(housesData).reduce((acc, house) => {
            // For specific houses, use house-specific calculation (useHouseSpecific = true)
            const houseResidents = combinedResidents.filter(
              (r) => r.house === house
            );
            acc[house] = Math.round(
              calculateAverageSoberDaysOnIntake(houseResidents, true)
            );
            return acc;
          }, {}),
        },
        medianSoberDaysOnIntake: {
          // For total, use the original intake date (useHouseSpecific = false)
          total: Math.round(
            calculateMedianSoberDaysOnIntake(combinedResidents, false)
          ),
          ...Object.keys(housesData).reduce((acc, house) => {
            // For specific houses, use house-specific calculation (useHouseSpecific = true)
            const houseResidents = combinedResidents.filter(
              (r) => r.house === house
            );
            acc[house] = Math.round(
              calculateMedianSoberDaysOnIntake(houseResidents, true)
            );
            return acc;
          }, {}),
        },
      };

      // Save snapshot to Firestore under the organization's collection
      const snapshotsRef = collection(
        db,
        `organizations/${organizationId}/weeklySnapshots`
      );
      const docRef = await addDoc(snapshotsRef, analyticsSnapshot);
      console.log("Snapshot saved successfully:", docRef.id);
      alert("Snapshot saved successfully!");
    } catch (error) {
      console.error("Error saving snapshot:", error);
      alert(
        `Error saving snapshot: ${error.message}. Check the console for details.`
      );
    }
  };

  // Helper to calculate length of stay
  const calculateLengthOfStay = (resident, houseName = null) => {
    if (!resident) return 0;

    // Check if filtering by house
    const isFilteringByHouse = typeof houseName === "string";

    // If no house history exists, fall back to `intakeDate` and `dischargeDate`
    if (!resident.houseHistory?.length) {
      const startDate = isFilteringByHouse
        ? moment(resident[`${houseName}MoveInDate`] || resident.intakeDate)
        : moment(resident.intakeDate);
      const endDate = resident.dischargeDate
        ? moment(resident.dischargeDate)
        : moment();

      return endDate.diff(startDate, "days");
    }

    // Filter houseHistory for relevant stays based on the house filter
    const relevantStays = resident.houseHistory.filter((h) => {
      if (!isFilteringByHouse) return true; // Include all stays if no house filter
      return (
        typeof h.house === "string" &&
        h.house.toLowerCase() === houseName.toLowerCase()
      );
    });

    // Calculate the total length of stay for relevant stays
    return relevantStays.reduce((total, stay) => {
      const moveInDate = moment(stay.moveInDate);
      const moveOutDate = stay.moveOutDate
        ? moment(stay.moveOutDate)
        : moment(resident.dischargeDate || moment());

      return total + moveOutDate.diff(moveInDate, "days");
    }, 0);
  };

  // Helper to calculate average stay dynamically
  const calculateAverageStay = (residents, houseName = null) => {
    const validResidents = residents.filter((resident) => {
      const stayLength = calculateLengthOfStay(resident, houseName);
      return stayLength >= 14; // Only include stays >= 14 days
    });

    const totalLengthOfStay = validResidents.reduce((total, resident) => {
      return total + calculateLengthOfStay(resident, houseName);
    }, 0);

    return validResidents.length > 0
      ? totalLengthOfStay / validResidents.length
      : 0;
  };

  // Helper to calculate median stay dynamically
  const calculateMedianStay = (residents, houseName = null) => {
    const stays = residents
      .map((resident) => calculateLengthOfStay(resident, houseName))
      .filter((stayLength) => stayLength >= 14)
      .sort((a, b) => a - b);

    if (stays.length === 0) return 0;

    const middle = Math.floor(stays.length / 2);
    return stays.length % 2 === 0
      ? (stays[middle - 1] + stays[middle]) / 2
      : stays[middle];
  };

  const processResidentData = async (residentsArray, housesData) => {
    const today = moment();

    // Helper to add sobriety times
    const addSobrietyTimes = (time1, time2) => {
      if (!time1 && !time2) return { years: 0, months: 0, days: 0 };
      if (!time1) return time2;
      if (!time2) return time1;

      const duration1 = moment.duration({
        years: time1.years || 0,
        months: time1.months || 0,
        days: time1.days || 0,
      });

      const duration2 = moment.duration({
        years: time2.years || 0,
        months: time2.months || 0,
        days: time2.days || 0,
      });

      const totalDuration = duration1.add(duration2);

      return {
        years: totalDuration.years(),
        months: totalDuration.months(),
        days: totalDuration.days(),
      };
    };

    // Initialize house data with default structure
    const houseData = Object.entries(housesData || {}).reduce(
      (acc, [houseName, houseInfo]) => {
        acc[houseName] = {
          occupiedBeds: 0,
          sobrietyTime: { years: 0, months: 0, days: 0 },
          lengthOfStay: 0,
          residentCount: 0,
        };
        return acc;
      },
      {}
    );

    // Aggregated statistics
    const stats = {
      matCount: { on: [], off: [] },
      probationCount: { on: [], off: [] },
      soberCount: { sober: [], relapsed: [] },
    };

    const ageRanges = {
      "18-25": { count: 0, residents: [] },
      "26-35": { count: 0, residents: [] },
      "36-45": { count: 0, residents: [] },
      "46-55": { count: 0, residents: [] },
      "56-65+": { count: 0, residents: [] },
    };

    // Initialize sobriety times
    const sobrietyTimes = Object.keys(housesData || {}).reduce((acc, house) => {
      acc[house] = { years: 0, months: 0, days: 0 };
      return acc;
    }, {});
    sobrietyTimes.total = { years: 0, months: 0, days: 0 };

    // Calculate relapse metrics
    const relapseResidents = residentsArray
      .filter(
        (resident) => resident.sobrietyStatus?.toLowerCase() === "not sober"
      )
      .filter((resident) => !!resident.dischargeDate)
      .sort((a, b) => moment(a.dischargeDate).diff(moment(b.dischargeDate)));

    // Calculate average and median time between relapses
    let averageRelapseDays = null;
    let medianRelapseDays = null;

    if (relapseResidents.length > 0) {
      // Calculate historical intervals between relapses
      const intervals = [];

      // Add the interval from program start to first relapse
      const programStartDate = await findProgramStartDate(organizationId);

      console.log("Program start date:", programStartDate);
      if (relapseResidents.length > 0) {
        console.log("First relapse date:", relapseResidents[0].dischargeDate);
      }

      if (programStartDate && relapseResidents.length > 0) {
        const firstRelapseDays = moment(relapseResidents[0].dischargeDate).diff(
          moment(programStartDate),
          "days"
        );
        if (firstRelapseDays > 0) {
          console.log(
            `Adding first relapse interval: ${firstRelapseDays} days from program start to first relapse`
          );
          intervals.push(firstRelapseDays);
        }
      }

      // Calculate intervals between relapses
      for (let i = 1; i < relapseResidents.length; i++) {
        const daysBetween = moment(relapseResidents[i].dischargeDate).diff(
          moment(relapseResidents[i - 1].dischargeDate),
          "days"
        );
        intervals.push(daysBetween);
      }

      // Process house streaks
      const houseStreaks = [];
      const housesWithRelapses = new Set();

      // Identify houses that have had relapses
      relapseResidents.forEach((resident) => {
        if (resident.house) {
          housesWithRelapses.add(resident.house);
        }
      });

      // For each house, find the most recent relapse and calculate days since
      housesWithRelapses.forEach((house) => {
        const houseRelapses = relapseResidents
          .filter((r) => r.house === house)
          .sort((a, b) =>
            moment(b.dischargeDate).diff(moment(a.dischargeDate))
          );

        if (houseRelapses.length > 0) {
          const mostRecentRelapse = houseRelapses[0];
          const daysSince = moment().diff(
            moment(mostRecentRelapse.dischargeDate),
            "days"
          );
          houseStreaks.push(daysSince);
        }
      });

      // For houses that have never had a relapse
      Object.keys(housesData).forEach((house) => {
        if (!housesWithRelapses.has(house)) {
          const houseResidents = residents.filter(
            (r) => r.house === house && r.currentResident
          );

          if (houseResidents.length > 0) {
            // Get all possible move-in dates for this house
            const moveInDates = [];

            houseResidents.forEach((resident) => {
              // Check houseHistory for this specific house
              if (
                resident.houseHistory &&
                Array.isArray(resident.houseHistory)
              ) {
                const houseEntries = resident.houseHistory.filter(
                  (entry) => entry.house === house && entry.moveInDate
                );

                if (houseEntries.length > 0) {
                  moveInDates.push(
                    ...houseEntries.map((entry) => entry.moveInDate)
                  );
                }
              }

              // Check for house-specific moveInDate field
              const houseField = house
                .replace(/[^a-zA-Z0-9]/g, "")
                .toLowerCase();
              const specificDateField = `${houseField}MoveInDate`;
              if (resident[specificDateField]) {
                moveInDates.push(resident[specificDateField]);
              }
            });

            // Only use general intakeDate as a last resort if we found no other dates
            if (moveInDates.length === 0) {
              const intakeDates = houseResidents
                .filter((r) => r.intakeDate)
                .map((r) => r.intakeDate);
              moveInDates.push(...intakeDates);
            }

            // Sort and find earliest date
            if (moveInDates.length > 0) {
              const earliestDate = moveInDates
                .filter((date) => date && moment(date).isValid())
                .sort((a, b) => moment(a).diff(moment(b)))[0];

              if (earliestDate) {
                const daysSince = moment().diff(moment(earliestDate), "days");
                houseStreaks.push({
                  fromName: `House Opening`,
                  toName: `Present (${house})`,
                  days: daysSince,
                  type: "current",
                });
              }
            }
          }
        }
      });

      // Fix for house streaks object problem - process any objects that might be in houseStreaks
      const validHouseStreaks = houseStreaks
        .map((streak) => {
          // If streak is an object with days property, return just the days value
          if (typeof streak === "object" && streak.days !== undefined) {
            return streak.days;
          }
          // If streak is already a number, return it directly
          return typeof streak === "number" ? streak : null;
        })
        .filter((days) => days !== null);

      // Combine historical intervals with current house streaks (using processed streaks)
      const allIntervals = [...intervals, ...validHouseStreaks];

      // Calculate average
      const totalDays = allIntervals.reduce((sum, days) => sum + days, 0);
      averageRelapseDays =
        allIntervals.length > 0 ? totalDays / allIntervals.length : null;

      // Calculate median
      const sortedIntervals = [...allIntervals].sort((a, b) => a - b);
      medianRelapseDays =
        sortedIntervals.length > 0
          ? sortedIntervals.length % 2 === 0
            ? (sortedIntervals[sortedIntervals.length / 2 - 1] +
                sortedIntervals[sortedIntervals.length / 2]) /
              2
            : sortedIntervals[Math.floor(sortedIntervals.length / 2)]
          : null;

      // Debug logs
      console.log("===== IMPROVED REOCCURRENCE CALCULATION =====");
      console.log(
        `Historical intervals between relapses: ${intervals.join(", ")}`
      );
      console.log(`Current house streaks: ${validHouseStreaks.join(", ")}`); // Show processed streaks
      console.log(
        `All intervals including current streaks: ${allIntervals.join(", ")}`
      );
      console.log(`New average: ${averageRelapseDays} days`);
      console.log(`New median: ${medianRelapseDays} days`);
      console.log("===== END IMPROVED CALCULATION =====");
    }

    // Process each resident
    residentsArray.forEach((resident) => {
      if (resident.firstName === "James" && resident.lastName === "Crow") {
        console.log("Processing Resident - James Crow:", resident);
      }
      // Calculate age demographics
      if (resident.dob) {
        const age = today.diff(moment(resident.dob), "years");
        const ageRange =
          age <= 25
            ? "18-25"
            : age <= 35
            ? "26-35"
            : age <= 45
            ? "36-45"
            : age <= 55
            ? "46-55"
            : "56-65+";
        ageRanges[ageRange].count++;
        ageRanges[ageRange].residents.push(resident);
      }

      // MAT data
      if (resident.isMAT) {
        stats.matCount.on.push(resident);
      } else {
        stats.matCount.off.push(resident);
      }

      // Probation/Parole data
      if (resident.poFirstName || resident.poLastName) {
        stats.probationCount.on.push(resident);
      } else {
        stats.probationCount.off.push(resident);
      }

      // Sobriety status
      if (resident.sobrietyStatus === "Sober") {
        stats.soberCount.sober.push(resident);
      } else if (resident.sobrietyStatus === "Not Sober") {
        stats.soberCount.relapsed.push(resident);
      }

      // House-specific data and sobriety times
      if (resident.house && houseData[resident.house]) {
        const houseStats = houseData[resident.house];
        if (resident.currentResident) {
          houseStats.occupiedBeds++;
          if (resident.sobrietyDate) {
            const sobrietyTime = calculateLengthOfSobriety(
              resident.sobrietyDate
            );

            houseStats.sobrietyTime = addSobrietyTimes(
              houseStats.sobrietyTime,
              sobrietyTime
            );
            sobrietyTimes[resident.house] = addSobrietyTimes(
              sobrietyTimes[resident.house],
              sobrietyTime
            );
          }
        }
        const stayLength = calculateLengthOfStay(
          resident.intakeDate,
          resident.dischargeDate
        );
        houseStats.lengthOfStay += stayLength;
        houseStats.residentCount++;
      }
    });

    //referral data
    const referralData = residentsArray.reduce((acc, resident) => {
      if (resident.referralLocation) {
        if (!acc[resident.referralLocation]) {
          acc[resident.referralLocation] = {
            count: 0,
            residents: [],
            fill: getRandomColor(),
          };
        }
        acc[resident.referralLocation].count++;
        acc[resident.referralLocation].residents.push(resident);
      }
      return acc;
    }, {});

    const processedReferralData = Object.entries(referralData).reduce(
      (acc, [location, data]) => {
        if (data.count === 1) {
          // If this location only has one referral, add it to "Other"
          if (!acc["Other"]) {
            acc["Other"] = {
              count: 0,
              residents: [],
              fill: getRandomColor(),
            };
          }
          acc["Other"].count += data.count;
          acc["Other"].residents.push(...data.residents);
        } else {
          // If the location has more than one referral, keep it as is
          acc[location] = data;
        }
        return acc;
      },
      {}
    );

    // Convert to array format and calculate percentages
    const referralDataArray = calculatePercentages(
      Object.entries(processedReferralData).map(([location, data]) => ({
        name: location,
        value: data.count,
        residents: data.residents,
        fill: data.fill,
      }))
    );

    // Calculate total sobriety after processing all residents
    sobrietyTimes.total = Object.entries(sobrietyTimes)
      .filter(([house]) => house !== "total")
      .reduce((total, [_, time]) => addSobrietyTimes(total, time), {
        years: 0,
        months: 0,
        days: 0,
      });

    // Calculate average and median lengths of stay for each house
    const averageLengthOfStay = Object.fromEntries(
      Object.entries(houseData).map(([houseName, houseStats]) => {
        const averageStay = calculateAverageStay(
          residentsArray.filter((r) => r.house === houseName),
          houseName
        );
        console.log(`House: ${houseName}, Average Stay: ${averageStay}`);
        return [houseName, averageStay];
      })
    );

    const medianLengthOfStay = Object.fromEntries(
      Object.entries(houseData).map(([houseName, houseStats]) => {
        const medianStay = calculateMedianStay(
          residentsArray.filter((r) => r.house === houseName),
          houseName
        );
        console.log(`House: ${houseName}, Median Stay: ${medianStay}`);
        return [houseName, medianStay];
      })
    );

    // Add total calculations for length of stay
    averageLengthOfStay.total = calculateAverageStay(residentsArray, (r) =>
      moment(r.intakeDate)
    );
    medianLengthOfStay.total = calculateMedianStay(residentsArray, (r) =>
      moment(r.intakeDate)
    );

    // Process sobriety status data
    const sobrietyStatusData = calculatePercentages([
      {
        name: "Sober",
        value: stats.soberCount.sober.length,
        residents: stats.soberCount.sober,
        fill: getRandomColor(),
      },
      {
        name: "Not Sober",
        value: stats.soberCount.relapsed.length,
        residents: stats.soberCount.relapsed,
        fill: getRandomColor(),
      },
    ]);

    // Process probation/parole data
    const probationParoleData = calculatePercentages([
      {
        name: "On Probation/Parole",
        value: stats.probationCount.on.length,
        residents: stats.probationCount.on,
        fill: getRandomColor(),
      },
      {
        name: "Not on Probation/Parole",
        value: stats.probationCount.off.length,
        residents: stats.probationCount.off,
        fill: getRandomColor(),
      },
    ]);

    // Process MAT data
    const matData = calculatePercentages([
      {
        name: "On MAT",
        value: stats.matCount.on.length,
        residents: stats.matCount.on,
        fill: getRandomColor(),
      },
      {
        name: "Not on MAT",
        value: stats.matCount.off.length,
        residents: stats.matCount.off,
        fill: getRandomColor(),
      },
    ]);

    // Process age demographic data
    const totalAgeCount = Object.values(ageRanges).reduce(
      (sum, range) => sum + range.count,
      0
    );

    const ageData = Object.entries(ageRanges).map(([range, data]) => ({
      name: range,
      value: data.count,
      residents: data.residents,
      fill: getRandomColor(),
      percentage: ((data.count / totalAgeCount) * 100).toFixed(1), // Add percentage
    }));

    // Process discharge status data
    const dischargeStatusData = calculatePercentages([
      {
        name: "Healthy Transition",
        value: residentsArray.filter(
          (r) => r.dischargeStatus?.toLowerCase() === "healthy transition"
        ).length,
        residents: residentsArray.filter(
          (r) => r.dischargeStatus?.toLowerCase() === "healthy transition"
        ),
        fill: getRandomColor(),
      },
      {
        name: "Against Advice",
        value: residentsArray.filter(
          (r) => r.dischargeStatus?.toLowerCase() === "against advice"
        ).length,
        residents: residentsArray.filter(
          (r) => r.dischargeStatus?.toLowerCase() === "against advice"
        ),
        fill: getRandomColor(),
      },
      {
        name: "Removed",
        value: residentsArray.filter(
          (r) => r.dischargeStatus?.toLowerCase() === "removed"
        ).length,
        residents: residentsArray.filter(
          (r) => r.dischargeStatus?.toLowerCase() === "removed"
        ),
        fill: getRandomColor(),
      },
    ]).filter((item) => item.value > 0);

    // Process medication counts and types
    const medicationCounts = residentsArray.reduce((acc, resident) => {
      let medCount = 0;
      if (Array.isArray(resident.medications)) {
        medCount = resident.medications.filter(
          (med) => med && med.name && med.name.trim() !== ""
        ).length;
      } else if (
        resident.medications &&
        typeof resident.medications === "object"
      ) {
        medCount = Object.values(resident.medications).filter(
          (med) => med && med.name && med.name.trim() !== ""
        ).length;
      }

      const key =
        medCount === 0
          ? "0 Medications"
          : `${medCount} Medication${medCount === 1 ? "" : "s"}`;
      if (!acc[key]) {
        acc[key] = {
          name: key,
          count: 0,
          value: 0,
          residents: [],
          fill: getRandomColor(),
        };
      }
      acc[key].count++;
      acc[key].value++;
      acc[key].residents.push(resident);
      return acc;
    }, {});

    // Process medication types
    const medicationTypes = residentsArray.reduce((acc, resident) => {
      const medications = Array.isArray(resident.medications)
        ? resident.medications
        : resident.medications && typeof resident.medications === "object"
        ? Object.values(resident.medications)
        : [];

      medications
        .filter((med) => med && med.name && med.name.trim() !== "")
        .forEach((med) => {
          const medName = med.name.trim();
          if (!acc[medName]) {
            acc[medName] = {
              name: medName,
              value: 0,
              count: 0,
              residents: [],
              fill: getRandomColor(),
            };
          }
          acc[medName].value++;
          acc[medName].count++;
          acc[medName].residents.push(resident);
        });
      return acc;
    }, {});

    // Add logic to group medications below threshold into "Other"
    const totalMedicationInstances = Object.values(medicationTypes).reduce(
      (sum, item) => sum + item.value,
      0
    );

    const processedMedicationTypes = Object.entries(medicationTypes).reduce(
      (acc, [medication, data]) => {
        const percentage = (data.value / totalMedicationInstances) * 100;

        // Update in the processedMedicationTypes section:
        if (percentage < 3) {
          // Medications that represent less than 5% go to "Other"
          if (!acc["Other"]) {
            acc["Other"] = {
              name: "Other",
              value: 0,
              count: 0,
              residents: [],
              medications: [], // Add this to track medication names with their residents
              fill: getRandomColor(),
            };
          }
          acc["Other"].value += data.value;
          acc["Other"].count += data.count;
          acc["Other"].residents.push(...data.residents);
          acc["Other"].medications.push({
            name: medication,
            count: data.value,
            residents: data.residents, // Include residents for each medication
          });
        } else {
          acc[medication] = data;
        }
        return acc;
      },
      {}
    );

    // Convert medication counts to array format
    const totalMedicationCount = Object.values(medicationCounts).reduce(
      (sum, item) => sum + item.value,
      0
    );

    const medicationData = Object.values(medicationCounts)
      .map((item) => ({
        ...item,
        percentage: ((item.value / totalMedicationCount) * 100).toFixed(1), // Add percentage
      }))
      .sort((a, b) => {
        if (a.name === "No Medications") return -1;
        if (b.name === "No Medications") return 1;
        const aNum = parseInt(a.name);
        const bNum = parseInt(b.name);
        return aNum - bNum;
      });

    // Convert medication types to array format with percentages
    const medicationTypeData = Object.values(processedMedicationTypes)
      .map((item) => ({
        ...item,
        percentage: ((item.value / totalMedicationInstances) * 100).toFixed(1),
      }))
      .sort((a, b) => {
        if (a.name === "Other") return 1; // Put "Other" at the end
        if (b.name === "Other") return -1;
        return b.value - a.value; // Sort by value descending
      });

    /* ---------- Substance-of-Choice (SoC) ---------- */
    const drugCounts = residentsArray.reduce((acc, resident) => {
      const list = Array.isArray(resident.drugOfChoice)
        ? resident.drugOfChoice
        : [];
      list
        .filter((d) => typeof d === "string" && d.trim() !== "")
        .forEach((drug) => {
          const key = drug.trim();
          if (!acc[key]) {
            acc[key] = {
              name: key,
              value: 0,
              residents: [],
              fill: getRandomColor(),
            };
          }
          acc[key].value += 1;
          acc[key].residents.push(resident);
        });
      return acc;
    }, {});

    // Skip grouping into "Other" for Substance of Choice
    const substanceOfChoiceData = calculatePercentages(
      Object.values(drugCounts)
    );

    return {
      ageDemographics: ageRanges,
      houseData,
      averageLengthOfStay,
      medianLengthOfStay,
      averageRelapseDays,
      medianRelapseDays,
      averageSoberDaysOnIntake: {
        // For total, use the original intake date (useHouseSpecific = false)
        total: calculateAverageSoberDaysOnIntake(residentsArray, false),
        ...Object.keys(housesData).reduce((acc, house) => {
          // For specific houses, use house-specific calculation (useHouseSpecific = true)
          const houseResidents = residentsArray.filter(
            (r) => r.house === house
          );
          acc[house] = calculateAverageSoberDaysOnIntake(houseResidents, true);
          return acc;
        }, {}),
      },
      medianSoberDaysOnIntake: {
        // For total, use the original intake date (useHouseSpecific = false)
        total: calculateMedianSoberDaysOnIntake(residentsArray, false),
        ...Object.keys(housesData).reduce((acc, house) => {
          // For specific houses, use house-specific calculation (useHouseSpecific = true)
          const houseResidents = residentsArray.filter(
            (r) => r.house === house
          );
          acc[house] = calculateMedianSoberDaysOnIntake(houseResidents, true);
          return acc;
        }, {}),
      },
      referralData: referralDataArray,
      stats,
      sobrietyStatusData,
      medicationData,
      medicationTypeData,
      probationParoleData,
      substanceOfChoiceData,
      matReoccurrenceData: processMatReoccurrenceData(residentsArray),
      recidivismData: processRecidivismData(residentsArray, "all"),
      sobrietyTimes,
      matData,
      ageData,
      dischargeStatusData,
    };
  };

  useEffect(() => {
    if (residents.length > 0 && Object.keys(housesData).length > 0) {
      // Apply filters if they're set
      const filteredResidents = residents.filter((resident) => {
        if (isCurrent && !selectedHouse) {
          return resident.currentResident;
        } else if (isCurrent && selectedHouse) {
          return resident.currentResident && resident.house === selectedHouse;
        } else if (!isCurrent && selectedHouse) {
          return resident.house === selectedHouse;
        } else {
          return true; // No filtering
        }
      });

      processResidentData(filteredResidents, housesData)
        .then((processedData) => {
          setAnalyticsData(processedData);
        })
        .catch((error) => {
          console.error("Error processing filtered data:", error);
        });
    }
  }, [residents, housesData, isCurrent, selectedHouse]);

  const processSobrietyDataWithRiskFilter = (
    residentsArray,
    riskFilter,
    statusFilter,
    shouldDeduplicate = false
  ) => {
    let filteredResidents = residentsArray;

    // Apply risk factor filter
    if (riskFilter !== "all") {
      filteredResidents = residentsArray.filter((resident) => {
        switch (riskFilter) {
          case "overOneYearInPrison":
            return resident.overOneYearInPrison === "Yes";
          case "homelessOneYear":
            return resident.homelessOneYear === "Yes";
          case "childhoodTrauma":
            return resident.childhoodTrauma === "Yes";
          case "twoParentsHome":
            return resident.twoParentsHome === "No";
          case "coOccurringDiagnosis":
            return resident.coOccurringDiagnosis === "Yes";
          default:
            return true;
        }
      });
      console.log("After risk filter:", filteredResidents.length);
    }

    // Apply deduplication if requested
    let deduplicatedResidents = filteredResidents;
    if (shouldDeduplicate) {
      console.log("=== DEDUPLICATION PROCESS ===");
      console.log("Residents before deduplication:", filteredResidents.length);

      // Log all fullName values
      console.log(
        "All fullName values:",
        filteredResidents.map((r) => r.fullName).filter((name) => name)
      );

      const nonDuplicates = [];
      const nameGroups = {};

      filteredResidents.forEach((resident, index) => {
        console.log(`Processing resident ${index}:`, {
          firstName: resident.firstName,
          lastName: resident.lastName,
          fullName: resident.fullName,
          currentResident: resident.currentResident,
        });

        if (!resident.fullName) {
          console.log(`  -> No fullName, adding to nonDuplicates`);
          nonDuplicates.push(resident);
          return;
        }

        const match = resident.fullName.match(/^(.+?)\s*\((\d+)\)$/);
        if (match) {
          const baseName = match[1].trim();
          const stayNumber = parseInt(match[2]);

          console.log(
            `  -> Found duplicate pattern: "${baseName}" stay #${stayNumber}`
          );

          if (
            !nameGroups[baseName] ||
            nameGroups[baseName].stayNumber < stayNumber
          ) {
            if (nameGroups[baseName]) {
              console.log(
                `    -> Replacing previous stay #${nameGroups[baseName].stayNumber} with #${stayNumber}`
              );
            } else {
              console.log(`    -> First entry for "${baseName}"`);
            }
            nameGroups[baseName] = { resident, stayNumber };
          } else {
            console.log(
              `    -> Keeping existing stay #${nameGroups[baseName].stayNumber}, ignoring #${stayNumber}`
            );
          }
        } else {
          console.log(
            `  -> No duplicate pattern found, adding to nonDuplicates`
          );
          nonDuplicates.push(resident);
        }
      });

      // Add the highest numbered stays to the deduplicated array
      console.log("=== FINAL DEDUPLICATION RESULTS ===");
      console.log("Name groups found:", Object.keys(nameGroups));
      Object.values(nameGroups).forEach(({ resident, stayNumber }) => {
        console.log(
          `Adding highest stay: ${resident.fullName} (stay #${stayNumber})`
        );
        nonDuplicates.push(resident);
      });

      deduplicatedResidents = nonDuplicates;
      console.log(
        "Residents after deduplication:",
        deduplicatedResidents.length
      );
      console.log(
        "Removed:",
        filteredResidents.length - deduplicatedResidents.length,
        "duplicates"
      );
    }

    // Apply status filter and determine sobriety status
    const residentsWithSobrietyStatus = deduplicatedResidents
      .filter((r) => {
        const hasSobrietyStatus =
          r.sobrietyStatus === "Sober" || r.sobrietyStatus === "Not Sober";
        return hasSobrietyStatus;
      })
      .map((resident) => {
        // Determine effective sobriety status based on filter
        let effectiveSobrietyStatus = resident.sobrietyStatus;

        if (statusFilter === "current") {
          // For current status: if stillSober field exists and is false, mark as "Not Sober"
          if (resident.stillSober === false) {
            effectiveSobrietyStatus = "Not Sober";
          }
          // If stillSober field doesn't exist, keep original sobrietyStatus
        }
        // For discharge status, use original sobrietyStatus as-is

        return { ...resident, effectiveSobrietyStatus };
      });

    console.log(
      "Final residents with sobriety status:",
      residentsWithSobrietyStatus.length
    );

    // Process sobriety status for filtered residents with valid status
    const soberResidents = residentsWithSobrietyStatus.filter(
      (r) => r.effectiveSobrietyStatus === "Sober"
    );
    const notSoberResidents = residentsWithSobrietyStatus.filter(
      (r) => r.effectiveSobrietyStatus === "Not Sober"
    );

    console.log("Sober residents:", soberResidents.length);
    console.log("Not sober residents:", notSoberResidents.length);
    console.log("=== END DEBUG ===");

    const sobrietyData = [
      {
        name: "Sober",
        value: soberResidents.length,
        residents: soberResidents,
        fill: "#4ADE80", // Green
      },
      {
        name: "Not Sober",
        value: notSoberResidents.length,
        residents: notSoberResidents,
        fill: "#FB7185", // Red
      },
    ];

    // Return both the chart data AND the count for display
    return {
      chartdata: calculatePercentages(sobrietyData),
      totalWithRiskFactorAndStatus: residentsWithSobrietyStatus.length,
    };
  };

  const processProbationParoleDataWithDeduplication = (residentsArray) => {
    console.log("=== PROBATION/PAROLE DEDUPLICATION ===");
    console.log("Input residents:", residentsArray.length);

    // DEDUPLICATE: Remove duplicates (names with parentheses)
    const deduplicatedResidents = [];
    const nameGroups = {};

    residentsArray.forEach((resident) => {
      if (!resident.fullName) {
        deduplicatedResidents.push(resident);
        return;
      }

      const match = resident.fullName.match(/^(.+?)\s*\((\d+)\)$/);
      if (match) {
        const baseName = match[1].trim();
        const stayNumber = parseInt(match[2]);

        console.log(`Found duplicate: ${resident.fullName}`);

        // Only keep the highest numbered stay for each person
        if (
          !nameGroups[baseName] ||
          nameGroups[baseName].stayNumber < stayNumber
        ) {
          nameGroups[baseName] = { resident, stayNumber };
        }
      } else {
        // Check if this is a base name that has duplicates
        const hasNumberedDuplicates = residentsArray.some(
          (r) => r.fullName && r.fullName.startsWith(resident.fullName + " (")
        );

        if (!hasNumberedDuplicates) {
          // This person has no duplicates, add them normally
          deduplicatedResidents.push(resident);
        } else {
          // This is the base version of someone who has duplicates
          // Only add if we haven't seen a numbered version yet
          if (!nameGroups[resident.fullName]) {
            nameGroups[resident.fullName] = { resident, stayNumber: 1 };
          }
        }
      }
    });

    // Add only the highest numbered stays from nameGroups
    Object.values(nameGroups).forEach(({ resident }) => {
      deduplicatedResidents.push(resident);
    });

    console.log("After deduplication:", deduplicatedResidents.length);
    console.log(
      "Removed duplicates:",
      residentsArray.length - deduplicatedResidents.length
    );

    // Process probation/parole status for deduplicated residents
    const onProbationParole = deduplicatedResidents.filter(
      (resident) => resident.poFirstName || resident.poLastName
    );
    const notOnProbationParole = deduplicatedResidents.filter(
      (resident) => !resident.poFirstName && !resident.poLastName
    );

    console.log("On Probation/Parole:", onProbationParole.length);
    console.log("Not on Probation/Parole:", notOnProbationParole.length);
    console.log("=== END PROBATION/PAROLE DEDUPLICATION ===");

    const probationParoleData = [
      {
        name: "On Probation/Parole",
        value: onProbationParole.length,
        residents: onProbationParole,
        fill: "#4ADE80", // Green
      },
      {
        name: "Not on Probation/Parole",
        value: notOnProbationParole.length,
        residents: notOnProbationParole,
        fill: "#FB7185", // Red
      },
    ];

    return calculatePercentages(probationParoleData);
  };

  // Handle house filter
  const handleHouseChange = (e) => {
    setSelectedHouse(e.target.value);
  };

  const openCalculationModal = async (title, contentType) => {
    let content;

    /* ─────────────────── REOCCURRENCE ─────────────────── */
    if (contentType === "reoccurrence") {
      const relapseResidents = residents
        .filter((r) => r.sobrietyStatus?.toLowerCase() === "not sober")
        .filter((r) => !!r.dischargeDate)
        .sort((a, b) => moment(a.dischargeDate).diff(moment(b.dischargeDate)));

      /* build relapse-to-relapse gaps */
      const historicalIntervals = [];
      for (let i = 1; i < relapseResidents.length; i++) {
        historicalIntervals.push({
          fromName: `${relapseResidents[i - 1].firstName} ${
            relapseResidents[i - 1].lastName
          }`,
          toName: `${relapseResidents[i].firstName} ${relapseResidents[i].lastName}`,
          days: moment(relapseResidents[i].dischargeDate).diff(
            moment(relapseResidents[i - 1].dischargeDate),
            "days"
          ),
          type: "historical",
        });
      }

      /* insert the very first span — Program Start → first relapse */
      const programStartDate = await findProgramStartDate(organizationId); // alumni only
      if (programStartDate && relapseResidents.length) {
        const firstSpanDays = moment(relapseResidents[0].dischargeDate).diff(
          moment(programStartDate),
          "days"
        );

        if (firstSpanDays > 0) {
          historicalIntervals.unshift({
            fromName: "Program Start",
            toName: `${relapseResidents[0].firstName} ${relapseResidents[0].lastName}`,
            days: firstSpanDays,
            type: "historical",
          });
        }
      }

      /* add current house-streak rows (unchanged) */
      const houseStreaks = [];
      const housesWithRelapses = new Set();
      relapseResidents.forEach(
        (r) => r.house && housesWithRelapses.add(r.house)
      );

      Object.keys(housesData).forEach((house) => {
        if (housesWithRelapses.has(house)) {
          const houseRelapses = relapseResidents
            .filter((r) => r.house === house)
            .sort((a, b) =>
              moment(b.dischargeDate).diff(moment(a.dischargeDate))
            );

          if (houseRelapses.length) {
            const mostRecent = houseRelapses[0];
            houseStreaks.push({
              fromName: `${mostRecent.firstName} ${mostRecent.lastName}`,
              toName: `Present (${house})`,
              days: moment().diff(moment(mostRecent.dischargeDate), "days"),
              type: "current",
            });
          }
        } else {
          /* house with zero relapses */
          const houseResidents = residents.filter(
            (r) => r.house === house && r.intakeDate && r.currentResident
          );
          if (houseResidents.length) {
            const earliest = houseResidents
              .map((r) => r.intakeDate)
              .sort((a, b) => moment(a).diff(moment(b)))[0];

            if (earliest) {
              houseStreaks.push({
                fromName: "House Opening",
                toName: `Present (${house})`,
                days: moment().diff(moment(earliest), "days"),
                type: "current",
              });
            }
          }
        }
      });

      /* combine for display */
      const allIntervals = [...historicalIntervals, ...houseStreaks];

      content = (
        <div className="space-y-4 text-white">
          <p className="text-cyan-400 font-medium">
            Based on {relapseResidents.length} reoccurrence events and{" "}
            {Object.keys(housesData).length} houses' current streaks
          </p>
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h3 className="text-lg font-medium mb-2">Calculation Method:</h3>
            <p>1. Sort all reoccurrence events by date</p>
            <p>2. Calculate the days between consecutive events</p>
            <p>
              3. Include the current streak without reoccurrences for each house
            </p>
            <p>
              4.{" "}
              {relapseDisplayType === "average"
                ? "Calculate the average of all intervals"
                : "Find the median value of all intervals"}
            </p>
          </div>
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h3 className="text-lg font-medium mb-2">Results:</h3>
            <div className="flex justify-between mb-3">
              <span>
                Average: {Math.round(analyticsData.averageRelapseDays || 0)}{" "}
                days
              </span>
              <span>
                Median: {Math.round(analyticsData.medianRelapseDays || 0)} days
              </span>
            </div>
          </div>
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h3 className="text-lg font-medium mb-2">Individual Intervals:</h3>
            <div className="max-h-60 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-left border-b border-slate-700">
                  <tr>
                    <th className="pb-2">From</th>
                    <th className="pb-2 text-center">Interval</th>
                    <th className="pb-2 text-right">To</th>
                  </tr>
                </thead>
                <tbody>
                  {allIntervals.map((item, i) => (
                    <tr
                      key={i}
                      className={`border-b border-slate-800/50 ${
                        item.type === "current" ? "bg-slate-800/30" : ""
                      }`}
                    >
                      <td className="py-1">{item.fromName}</td>
                      <td
                        className={`py-1 text-center ${
                          item.type === "current"
                            ? "text-emerald-400"
                            : "text-cyan-400"
                        }`}
                      >
                        {item.days} days
                      </td>
                      <td className="py-1 text-right">{item.toName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    } else if (contentType === "soberDays") {
      // Existing soberDays code...
      // Get the relevant data
      const houseKey = selectedHouse || "total";
      const relevantResidents = residents.filter((resident) =>
        selectedHouse ? resident.house === selectedHouse : true
      );

      // Get the individual data points for display
      const soberDaysArray = relevantResidents
        .map((resident) => ({
          name: `${resident.firstName} ${resident.lastName}`,
          days:
            calculateSoberDaysOnIntake(
              resident,
              selectedHouse ? true : false
            ) || 0,
          formattedDays: formatDaysToMonthsAndDays(
            calculateSoberDaysOnIntake(
              resident,
              selectedHouse ? true : false
            ) || 0
          ),
        }))
        .filter((item) => item.days > 0)
        .sort((a, b) => b.days - a.days);

      const averageDays = analyticsData.averageSoberDaysOnIntake[houseKey] || 0;
      const medianDays = analyticsData.medianSoberDaysOnIntake[houseKey] || 0;

      content = (
        <div className="space-y-4 text-white">
          <p className="text-cyan-400 font-medium">
            Based on {soberDaysArray.length} residents with sobriety date before
            intake
          </p>
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h3 className="text-lg font-medium mb-2">Calculation Method:</h3>
            <p>
              1. For each resident, calculate days between sobriety date and
              intake date
            </p>
            <p>
              2. Only include residents with sobriety date before intake
              (positive values)
            </p>
            <p>
              3.{" "}
              {soberDaysDisplayType === "average"
                ? "Sum all days and divide by the number of residents (average)"
                : "Sort all values and find the middle value (median)"}
            </p>
          </div>
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h3 className="text-lg font-medium mb-2">Results:</h3>
            <div className="flex justify-between mb-3">
              <span>Average: {formatDaysToMonthsAndDays(averageDays)}</span>
              <span>Median: {formatDaysToMonthsAndDays(medianDays)}</span>
            </div>
          </div>
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h3 className="text-lg font-medium mb-2">
              Individual Data Points:
            </h3>
            <div className="max-h-60 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-left border-b border-slate-700">
                  <tr>
                    <th className="pb-2">Resident</th>
                    <th className="pb-2 text-right">Sober Days on Intake</th>
                  </tr>
                </thead>
                <tbody>
                  {soberDaysArray.map((item, i) => (
                    <tr key={i} className="border-b border-slate-800/50">
                      <td className="py-1">{item.name}</td>
                      <td className="py-1 text-right text-cyan-400">
                        {item.formattedDays}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    } else if (contentType === "lengthOfStay") {
      // Existing lengthOfStay code...
      // Get the relevant data
      const houseKey = selectedHouse || "total";
      const relevantResidents = residents.filter((resident) =>
        selectedHouse ? resident.house === selectedHouse : true
      );

      // Calculate stay lengths for display
      const stayLengthArray = relevantResidents
        .map((resident) => ({
          name: `${resident.firstName} ${resident.lastName}`,
          days: calculateLengthOfStay(resident, selectedHouse || null),
          formattedDays: formatDaysToMonthsAndDays(
            calculateLengthOfStay(resident, selectedHouse || null)
          ),
        }))
        .filter((item) => item.days >= 14) // Only include stays >= 14 days
        .sort((a, b) => b.days - a.days);

      const averageDays = analyticsData.averageLengthOfStay[houseKey] || 0;
      const medianDays = analyticsData.medianLengthOfStay[houseKey] || 0;

      content = (
        <div className="space-y-4 text-white">
          <p className="text-cyan-400 font-medium">
            Based on {stayLengthArray.length} residents with stays of 14+ days
          </p>
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h3 className="text-lg font-medium mb-2">Calculation Method:</h3>
            <p>
              1. For each resident, calculate days between move-in date and
              move-out date (or current date)
            </p>
            <p>
              2. For residents with house history, calculate based on stays in
              the selected house
            </p>
            <p>3. Only include residents with stays of at least 14 days</p>
            <p>
              4.{" "}
              {stayDisplayType === "average"
                ? "Sum all stay lengths and divide by the number of residents (average)"
                : "Sort all values and find the middle value (median)"}
            </p>
          </div>
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h3 className="text-lg font-medium mb-2">Results:</h3>
            <div className="flex justify-between mb-3">
              <span>Average: {formatDaysToMonthsAndDays(averageDays)}</span>
              <span>Median: {formatDaysToMonthsAndDays(medianDays)}</span>
            </div>
          </div>
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h3 className="text-lg font-medium mb-2">
              Individual Data Points:
            </h3>
            <div className="max-h-60 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-left border-b border-slate-700">
                  <tr>
                    <th className="pb-2">Resident</th>
                    <th className="pb-2 text-right">Length of Stay</th>
                  </tr>
                </thead>
                <tbody>
                  {stayLengthArray.map((item, i) => (
                    <tr key={i} className="border-b border-slate-800/50">
                      <td className="py-1">{item.name}</td>
                      <td className="py-1 text-right text-cyan-400">
                        {item.formattedDays}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    }

    setCalculationTitle(title);
    setCalculationContent(content);
    setCalculationModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Spinner />
      </div>
    );
  }

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <>
      <Helmet>
        <title>&bull; Analytics</title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 p-6 relative overflow-visible">
        {/* Ambient background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto space-y-8 relative overflow-visible">
          {/* Header */}
          <div className="flex items-center gap-6 mb-12">
            <div className="flex gap-3">
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">
                Analytics Dashboard
              </h1>
            </div>
          </div>

          {/* Action buttons and filters */}
          <div className="grid grid-cols-1 gap-4 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Save Snapshot Button */}
              {checkPermission("saveSnapshots") && (
                <button
                  onClick={saveWeeklySnapshot}
                  className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-cyan-500/25 font-medium flex items-center justify-center gap-2"
                >
                  <Activity className="w-4 h-4" />
                  Save Snapshot
                </button>
              )}

              {/* Data Tracker Button */}
              {checkPermission("canViewAnalyticData") && (
                <a
                  href="/datatracker"
                  className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-slate-500/25 font-medium flex items-center justify-center gap-2"
                >
                  <BarChart3 className="w-4 h-4" />
                  Data Tracker
                </a>
              )}
            </div>

            <div className="flex flex-col md:flex-row gap-4 md:justify-end">
              <div className="grid grid-cols-2 md:grid-cols-2 gap-4 w-full md:w-auto max-w-2xl ml-auto">
                <select
                  value={selectedHouse}
                  onChange={handleHouseChange}
                  className="w-full px-4 py-3 bg-slate-800/50 backdrop-blur-xl text-white rounded-xl border border-slate-700/50 hover:border-slate-600/50 transition-all"
                >
                  <option value="">All Houses</option>
                  {Object.keys(housesData).map((house) => (
                    <option key={house} value={house}>
                      {house}
                    </option>
                  ))}
                </select>

                <select
                  onChange={(e) => setIsCurrent(e.target.value === "true")}
                  className="w-full px-4 py-3 bg-slate-800/50 backdrop-blur-xl text-white rounded-xl border border-slate-700/50 hover:border-slate-600/50 transition-all"
                >
                  <option value="">All Residents</option>
                  <option value="true">Current Residents</option>
                </select>
              </div>
            </div>
          </div>

          {/* Main stats grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Relapse Span Card */}
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-center p-4 border-b border-cyan-500/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-500/10 rounded-lg">
                    <Clock className="text-cyan-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    Reoccurrence Span
                  </h2>
                </div>
              </div>
              {/* Added min-h-[320px] and justify-center */}
              <div className="relative p-6 flex flex-col items-center min-h-[320px] justify-center z-10 text-center">
                <div className="flex justify-center items-baseline gap-10 mb-2">
                  <span className="text-5xl font-bold text-cyan-400">
                    {Math.floor(
                      (relapseDisplayType === "average"
                        ? analyticsData.averageRelapseDays || 0
                        : analyticsData.medianRelapseDays || 0) / 30
                    )}
                  </span>
                  <span className="text-5xl font-bold text-cyan-400">
                    {Math.round(
                      (relapseDisplayType === "average"
                        ? analyticsData.averageRelapseDays || 0
                        : analyticsData.medianRelapseDays || 0) % 30
                    )}
                  </span>
                </div>
                <span className="text-xl text-slate-400 mb-4 flex gap-6">
                  <span>Months</span>
                  <span>Days</span>
                </span>
                <div className="relative z-20 flex gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setRelapseDisplayType("average");
                    }}
                    className={`px-4 py-2 rounded-xl transition-all ${
                      relapseDisplayType === "average"
                        ? "bg-slate-500 text-white"
                        : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                    }`}
                  >
                    Average
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setRelapseDisplayType("median");
                    }}
                    className={`px-4 py-2 rounded-xl transition-all ${
                      relapseDisplayType === "median"
                        ? "bg-slate-500 text-white"
                        : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                    }`}
                  >
                    Median
                  </button>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openCalculationModal(
                      "Reoccurrence Span Calculation",
                      "reoccurrence"
                    );
                  }}
                  className="text-xs text-cyan-400 hover:text-cyan-300 underline transition-colors mt-2"
                >
                  See Calculation
                </button>
              </div>
            </div>
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-center p-4 border-b border-cyan-500/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-500/10 rounded-lg">
                    <Clock className="text-cyan-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    Sober Days on Intake
                  </h2>
                </div>
              </div>
              <div className="relative p-6 flex flex-col items-center min-h-[320px] justify-center z-10 text-center">
                <div className="flex justify-center items-baseline gap-10 mb-2">
                  <span className="text-5xl font-bold text-cyan-400">
                    {Math.floor(
                      (soberDaysDisplayType === "average"
                        ? analyticsData.averageSoberDaysOnIntake?.[
                            selectedHouse || "total"
                          ] || 0
                        : analyticsData.medianSoberDaysOnIntake?.[
                            selectedHouse || "total"
                          ] || 0) / 30
                    )}
                  </span>
                  <span className="text-5xl font-bold text-cyan-400">
                    {Math.round(
                      (soberDaysDisplayType === "average"
                        ? analyticsData.averageSoberDaysOnIntake?.[
                            selectedHouse || "total"
                          ] || 0
                        : analyticsData.medianSoberDaysOnIntake?.[
                            selectedHouse || "total"
                          ] || 0) % 30
                    )}
                  </span>
                </div>
                <span className="text-xl text-slate-400 mb-4 flex gap-6">
                  <span>Months</span>
                  <span>Days</span>
                </span>
                <div className="relative z-20 flex flex-col gap-2">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSoberDaysDisplayType("average");
                      }}
                      className={`px-4 py-2 rounded-xl transition-all ${
                        soberDaysDisplayType === "average"
                          ? "bg-slate-500 text-white"
                          : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                      }`}
                    >
                      Average
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSoberDaysDisplayType("median");
                      }}
                      className={`px-4 py-2 rounded-xl transition-all ${
                        soberDaysDisplayType === "median"
                          ? "bg-slate-500 text-white"
                          : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                      }`}
                    >
                      Median
                    </button>
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openCalculationModal(
                        "Sober Days on Intake Calculation",
                        "soberDays"
                      );
                    }}
                    className="text-xs text-cyan-400 hover:text-cyan-300 underline transition-colors mt-2"
                  >
                    See Calculation
                  </button>
                </div>
              </div>
            </div>

            {/* Length of Stay Card */}
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-center p-4 border-b border-cyan-500/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-500/10 rounded-lg">
                    <Timer className="text-cyan-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    Length of Stay
                  </h2>
                </div>
              </div>
              {/* Added min-h-[320px] and justify-center */}
              <div className="relative p-6 flex flex-col items-center min-h-[320px] justify-center z-10 text-center">
                <div className="flex justify-center items-baseline gap-10 mb-2">
                  <span className="text-5xl font-bold text-cyan-400">
                    {Math.floor(
                      (stayDisplayType === "average"
                        ? analyticsData.averageLengthOfStay[
                            selectedHouse || "total"
                          ] || 0
                        : analyticsData.medianLengthOfStay[
                            selectedHouse || "total"
                          ] || 0) / 30
                    )}
                  </span>
                  <span className="text-5xl font-bold text-cyan-400">
                    {Math.round(
                      (stayDisplayType === "average"
                        ? analyticsData.averageLengthOfStay[
                            selectedHouse || "total"
                          ] || 0
                        : analyticsData.medianLengthOfStay[
                            selectedHouse || "total"
                          ] || 0) % 30
                    )}
                  </span>
                </div>
                <span className="text-xl text-slate-400 mb-4 flex gap-6">
                  <span>Months</span>
                  <span>Days</span>
                </span>
                <div className="relative z-20 flex flex-col gap-2">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setStayDisplayType("average");
                      }}
                      className={`px-4 py-2 rounded-xl transition-all ${
                        stayDisplayType === "average"
                          ? "bg-slate-500 text-white"
                          : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                      }`}
                    >
                      Average
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setStayDisplayType("median");
                      }}
                      className={`px-4 py-2 rounded-xl transition-all ${
                        stayDisplayType === "median"
                          ? "bg-slate-500 text-white"
                          : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                      }`}
                    >
                      Median
                    </button>
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openCalculationModal(
                        "Length of Stay Calculation",
                        "lengthOfStay"
                      );
                    }}
                    className="text-xs text-cyan-400 hover:text-cyan-300 underline transition-colors mt-2"
                  >
                    See Calculation
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 overflow-visible">
            {/* Referral Data Chart */}
            <div
              onClick={() => {
                const referralData = analyticsData.referralData || [];
                if (referralData.length > 0) {
                  openModalChart(referralData, "pie", "Referral Data");
                } else {
                  console.warn("No referral data available to display.");
                }
              }}
              className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500 cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-center p-4 border-b border-cyan-500/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <PhoneCall className="text-purple-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    Referral Data
                  </h2>
                </div>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analyticsData.referralData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label
                    >
                      {analyticsData.referralData?.map((entry, index) => (
                        <Cell key={index} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recovery Counter Card */}
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500 cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-center p-4 border-b border-cyan-500/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-500/10 rounded-lg">
                    <Calculator className="text-emerald-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    Recovery Counter
                  </h2>
                </div>
              </div>
              <div className="p-6">
                {analyticsData.sobrietyTimes ? (
                  <div className="flex flex-col gap-3">
                    <div className="h-[240px] overflow-y-auto custom-scrollbar flex items-center">
                      <div
                        className={`grid gap-3 pr-2 w-full ${getRecoveryGridLayout()}`}
                      >
                        {[
                          ...Object.entries(housesData),
                          ...(Object.keys(housesData).length > 1
                            ? [["total", { color: "rgb(168, 85, 247)" }]]
                            : []),
                        ].map(([house, houseInfo]) => {
                          const fontSizes = getRecoveryFontSizes();
                          const isTotal = house === "total";

                          return (
                            <div
                              key={house}
                              style={{
                                backgroundColor: isTotal
                                  ? "rgba(168, 85, 247, 0.2)"
                                  : `${houseInfo.color}20` || "#64748b20",
                              }}
                              className={`group relative p-3 flex flex-col items-center justify-center cursor-pointer rounded-lg transition-all duration-300 hover:bg-opacity-30 ${getRecoveryCardHeight()} w-full`}
                              onClick={(e) => {
                                e.stopPropagation();
                                openHouseModal(house);
                              }}
                            >
                              <h3
                                className={`${fontSizes.houseName} text-white font-semibold text-center`}
                              >
                                {isTotal ? "Total" : house}
                              </h3>
                              <p
                                className={`${fontSizes.sobrietyTime} text-slate-300`}
                              >
                                {isTotal
                                  ? analyticsData.sobrietyTimes?.total
                                    ? `${analyticsData.sobrietyTimes.total.years}y ${analyticsData.sobrietyTimes.total.months}m ${analyticsData.sobrietyTimes.total.days}d`
                                    : "0y 0m 0d"
                                  : analyticsData.sobrietyTimes[house]
                                  ? `${analyticsData.sobrietyTimes[house].years}y ${analyticsData.sobrietyTimes[house].months}m ${analyticsData.sobrietyTimes[house].days}d`
                                  : "0y 0m 0d"}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-48">
                    <Spinner />
                  </div>
                )}
              </div>
            </div>

            {/* Enhanced Sobriety Status Chart with Risk Factor Filter */}
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Header with dropdowns */}
              <div className="relative p-4 border-b border-cyan-500/30">
                {/* Centered Title */}
                <div className="flex items-center justify-center mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-500/10 rounded-lg">
                      <UserCheck className="text-emerald-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">
                      Sobriety Status
                    </h2>
                  </div>
                </div>

                {/* Filter Dropdowns and Checkbox */}
                <div className="flex items-center justify-center gap-2 mb-2 flex-wrap">
                  {/* Status Filter Dropdown */}
                  <select
                    value={currentStatusFilter}
                    onChange={(e) => {
                      e.stopPropagation();
                      setCurrentStatusFilter(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="px-3 py-1 bg-slate-800/80 border border-slate-700 rounded-lg text-white text-sm hover:border-slate-600 transition-colors cursor-pointer"
                  >
                    <option value="discharge">On Discharge</option>
                    <option value="current">Current Status</option>
                  </select>

                  {/* Risk Factor Filter Dropdown */}
                  <select
                    value={riskFactorFilter}
                    onChange={(e) => {
                      e.stopPropagation();
                      setRiskFactorFilter(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="px-3 py-1 bg-slate-800/80 border border-slate-700 rounded-lg text-white text-sm hover:border-slate-600 transition-colors cursor-pointer"
                  >
                    <option value="all">All Residents</option>
                    <option value="overOneYearInPrison">
                      Over 1 Year in Prison
                    </option>
                    <option value="homelessOneYear">
                      Homeless Over 1 Year
                    </option>
                    <option value="childhoodTrauma">Childhood Trauma</option>
                    <option value="twoParentsHome">
                      Single Parent Household
                    </option>
                    <option value="coOccurringDiagnosis">
                      Mental Health Diagnosis
                    </option>
                  </select>
                </div>

                {/* Filter indicators */}
                <div className="flex gap-4 text-xs">
                  {riskFactorFilter !== "all" && (
                    <div className="text-cyan-400">
                      Risk Factor:{" "}
                      {riskFactorFilter === "overOneYearInPrison"
                        ? "Over 1 Year in Prison"
                        : riskFactorFilter === "homelessOneYear"
                        ? "Homeless Over 1 Year"
                        : riskFactorFilter === "childhoodTrauma"
                        ? "Childhood Trauma"
                        : riskFactorFilter === "twoParentsHome"
                        ? "Single Parent Household"
                        : riskFactorFilter === "coOccurringDiagnosis"
                        ? "Mental Health Diagnosis"
                        : "Unknown"}
                    </div>
                  )}
                  {deduplicateResidents && (
                    <div className="text-purple-400">Duplicates Removed</div>
                  )}
                </div>
                {/* Deduplication Checkbox */}
                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer hover:text-white transition-colors">
                  <input
                    type="checkbox"
                    checked={deduplicateResidents}
                    onChange={(e) => {
                      e.stopPropagation();
                      setDeduplicateResidents(e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 rounded border border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500 focus:ring-2"
                  />
                  <span>Remove Duplicates</span>
                </label>
              </div>

              {/* Chart area - clickable */}
              <div
                className="p-6 cursor-pointer"
                onClick={() => {
                  const currentResidents = residents.filter((resident) => {
                    if (isCurrent && !selectedHouse) {
                      return resident.currentResident;
                    } else if (isCurrent && selectedHouse) {
                      return (
                        resident.currentResident &&
                        resident.house === selectedHouse
                      );
                    } else if (!isCurrent && selectedHouse) {
                      return resident.house === selectedHouse;
                    } else {
                      return true;
                    }
                  });

                  const filteredResult = processSobrietyDataWithRiskFilter(
                    currentResidents,
                    riskFactorFilter,
                    currentStatusFilter,
                    deduplicateResidents // Add this parameter
                  );

                  const statusText =
                    currentStatusFilter === "discharge"
                      ? "On Discharge"
                      : "Current Status";
                  const riskText =
                    riskFactorFilter === "all"
                      ? ""
                      : ` - ${
                          riskFactorFilter === "overOneYearInPrison"
                            ? "Over 1 Year in Prison"
                            : riskFactorFilter === "homelessOneYear"
                            ? "Homeless Over 1 Year"
                            : riskFactorFilter === "childhoodTrauma"
                            ? "Childhood Trauma"
                            : riskFactorFilter === "twoParentsHome"
                            ? "Single Parent Household"
                            : riskFactorFilter === "coOccurringDiagnosis"
                            ? "Mental Health Diagnosis"
                            : "Filtered"
                        }`;
                  const title = `Sobriety Status - ${statusText}${riskText}`;

                  openModalChart(filteredResult.chartdata, "pie", title);
                }}
              >
                {(() => {
                  const currentResidents = residents.filter((resident) => {
                    if (isCurrent && !selectedHouse) {
                      return resident.currentResident;
                    } else if (isCurrent && selectedHouse) {
                      return (
                        resident.currentResident &&
                        resident.house === selectedHouse
                      );
                    } else if (!isCurrent && selectedHouse) {
                      return resident.house === selectedHouse;
                    } else {
                      return true;
                    }
                  });

                  const filteredResult = processSobrietyDataWithRiskFilter(
                    currentResidents,
                    riskFactorFilter,
                    currentStatusFilter,
                    deduplicateResidents // Add this parameter
                  );

                  return (
                    <>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={filteredResult.chartdata}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label
                          >
                            {filteredResult.chartdata.map((entry, index) => (
                              <Cell key={index} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            wrapperStyle={{
                              paddingTop: "20px",
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>

                      {/* Show count of filtered residents with corrected counting */}
                      {riskFactorFilter !== "all" && (
                        <div className="mt-2 text-center text-xs text-slate-400">
                          {filteredResult.totalWithRiskFactorAndStatus}{" "}
                          residents with this risk factor and sobriety status
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Discharge Status Chart */}
            <div
              onClick={() =>
                openModalChart(
                  analyticsData.dischargeStatusData,
                  "pie",
                  "Discharge Status"
                )
              }
              className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500 cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-center p-4 border-b border-cyan-500/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-500/10 rounded-lg">
                    <LogOut className="text-red-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    Discharge Status
                  </h2>
                </div>
              </div>
              <div className="relative p-6">
                <div
                  className="relative overflow-visible"
                  style={{ width: "100%", height: 300 }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analyticsData.dischargeStatusData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label
                      >
                        {analyticsData.dischargeStatusData?.map(
                          (entry, index) => (
                            <Cell key={index} fill={entry.fill} />
                          )
                        )}
                      </Pie>
                      <Tooltip
                        content={<CustomTooltip />}
                        position={{ y: -10 }}
                        wrapperStyle={{ zIndex: 1000 }}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        wrapperStyle={{
                          paddingTop: "20px",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* MAT Data Chart */}
            <div
              onClick={() =>
                openModalChart(analyticsData.matData, "pie", "MAT Data")
              }
              className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500 cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-center p-4 border-b border-cyan-500/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Stethoscope className="text-purple-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">MAT Data</h2>
                </div>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analyticsData.matData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label
                    >
                      {analyticsData.matData?.map((entry, index) => (
                        <Cell key={index} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      wrapperStyle={{
                        paddingTop: "20px",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Age Demographic Chart */}
            <div
              onClick={() =>
                openModalChart(analyticsData.ageData, "bar", "Age Demographic")
              }
              className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500 cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-center p-4 border-b border-cyan-500/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-500/10 rounded-lg">
                    <Users className="text-emerald-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    Age Demographic
                  </h2>
                </div>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analyticsData.ageData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.1)"
                    />
                    <XAxis
                      dataKey="name"
                      stroke="#94a3b8"
                      tick={{ fill: "#94a3b8" }}
                    />
                    <YAxis stroke="#94a3b8" tick={{ fill: "#94a3b8" }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value">
                      {analyticsData.ageData?.map((entry, index) => (
                        <Cell key={index} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Probation/Parole Chart */}
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Header with info tooltip */}
              <div className="relative p-4 border-b border-cyan-500/30">
                <div className="flex items-center justify-center">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-cyan-500/10 rounded-lg">
                      <Scale className="text-cyan-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">
                      Probation/Parole
                    </h2>
                    {/* Info Icon with Tooltip */}
                    <div className="relative">
                      <button
                        onMouseEnter={() => setShowProbationParoleInfo(true)}
                        onMouseLeave={() => setShowProbationParoleInfo(false)}
                        onClick={(e) => e.stopPropagation()}
                        className="p-1 text-slate-400 hover:text-cyan-400 transition-colors"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>

                      {/* Tooltip */}
                      {showProbationParoleInfo && (
                        <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-80 bg-slate-800 border border-slate-600 rounded-lg p-3 text-sm text-slate-200 shadow-lg z-50">
                          <div className="space-y-2">
                            <p className="font-medium text-cyan-400">
                              Deduplicated Data
                            </p>
                            <p>
                              This chart automatically removes duplicate
                              residents to ensure each person is counted only
                              once.
                            </p>
                            <p>
                              For residents with multiple stays only the most
                              recent stay data is used to prevent inflating the
                              probation/parole statistics.
                            </p>
                          </div>
                          {/* Arrow pointer */}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Chart area - clickable */}
              <div
                className="p-6 cursor-pointer"
                onClick={() => {
                  const currentResidents = residents.filter((resident) => {
                    if (isCurrent && !selectedHouse) {
                      return resident.currentResident;
                    } else if (isCurrent && selectedHouse) {
                      return (
                        resident.currentResident &&
                        resident.house === selectedHouse
                      );
                    } else if (!isCurrent && selectedHouse) {
                      return resident.house === selectedHouse;
                    } else {
                      return true;
                    }
                  });

                  // Always use deduplication for probation/parole chart
                  const probationParoleData =
                    processProbationParoleDataWithDeduplication(
                      currentResidents
                    );
                  openModalChart(
                    probationParoleData,
                    "pie",
                    "Probation/Parole (Deduplicated)"
                  );
                }}
              >
                {(() => {
                  const currentResidents = residents.filter((resident) => {
                    if (isCurrent && !selectedHouse) {
                      return resident.currentResident;
                    } else if (isCurrent && selectedHouse) {
                      return (
                        resident.currentResident &&
                        resident.house === selectedHouse
                      );
                    } else if (!isCurrent && selectedHouse) {
                      return resident.house === selectedHouse;
                    } else {
                      return true;
                    }
                  });

                  // Always use deduplication for probation/parole chart
                  const probationParoleData =
                    processProbationParoleDataWithDeduplication(
                      currentResidents
                    );

                  return (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={probationParoleData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label
                        >
                          {probationParoleData.map((entry, index) => (
                            <Cell key={index} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          wrapperStyle={{
                            paddingTop: "20px",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  );
                })()}
              </div>
            </div>

            {/* Employment Status */}
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-center p-4 border-b border-cyan-500/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-500/10 rounded-lg">
                    <Briefcase className="text-cyan-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    Employment Stats
                  </h2>
                </div>
              </div>

              {/* Tab buttons */}
              <div className="flex border-b border-slate-700/50 relative z-10">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setViewType("intake");
                  }}
                  className={`flex-1 py-2 text-center transition-colors ${
                    viewType === "intake"
                      ? "text-cyan-400 border-b-2 border-cyan-400"
                      : "text-slate-400 hover:text-slate-300"
                  }`}
                >
                  Intake
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setViewType("discharge");
                  }}
                  className={`flex-1 py-2 text-center transition-colors ${
                    viewType === "discharge"
                      ? "text-cyan-400 border-b-2 border-cyan-400"
                      : "text-slate-400 hover:text-slate-300"
                  }`}
                >
                  Discharge
                </button>
              </div>

              <div className="p-6">
                <div className="flex flex-col items-center mb-4">
                  <span className="text-4xl font-bold text-cyan-400">
                    {employedPercentage}%
                  </span>
                  <span className="text-slate-400">
                    Employed at {viewType === "intake" ? "Intake" : "Discharge"}
                  </span>
                </div>

                {/* Make the chart area clickable instead of the whole card */}
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    const employmentData = processEmploymentData(
                      viewType === "intake"
                        ? residents.filter((resident) =>
                            isCurrent && !selectedHouse
                              ? resident.currentResident
                              : isCurrent && selectedHouse
                              ? resident.currentResident &&
                                resident.house === selectedHouse
                              : !isCurrent && selectedHouse
                              ? resident.house === selectedHouse
                              : true
                          )
                        : residents.filter(
                            (resident) =>
                              !resident.currentResident && // Changed from currentResident to !currentResident
                              (selectedHouse
                                ? resident.house === selectedHouse
                                : true)
                          ),
                      viewType
                    );
                    openModalChart(
                      employmentData,
                      "pie",
                      `Employment at ${
                        viewType === "intake" ? "Intake" : "Discharge"
                      }`
                    );
                  }}
                >
                  <ResponsiveContainer width="100%" height={230}>
                    <PieChart>
                      <Pie
                        data={processEmploymentData(
                          viewType === "intake"
                            ? residents.filter((resident) =>
                                isCurrent && !selectedHouse
                                  ? resident.currentResident
                                  : isCurrent && selectedHouse
                                  ? resident.currentResident &&
                                    resident.house === selectedHouse
                                  : !isCurrent && selectedHouse
                                  ? resident.house === selectedHouse
                                  : true
                              )
                            : residents.filter(
                                (resident) =>
                                  !resident.currentResident && // Changed from currentResident to !currentResident
                                  (selectedHouse
                                    ? resident.house === selectedHouse
                                    : true)
                              ),
                          viewType
                        )}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label
                      >
                        {processEmploymentData(
                          viewType === "intake"
                            ? residents.filter((resident) =>
                                isCurrent && !selectedHouse
                                  ? resident.currentResident
                                  : isCurrent && selectedHouse
                                  ? resident.currentResident &&
                                    resident.house === selectedHouse
                                  : !isCurrent && selectedHouse
                                  ? resident.house === selectedHouse
                                  : true
                              )
                            : residents.filter(
                                (resident) =>
                                  resident.currentResident &&
                                  (selectedHouse
                                    ? resident.house === selectedHouse
                                    : true)
                              ),
                          viewType
                        ).map((entry, index) => (
                          <Cell key={index} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        wrapperStyle={{
                          paddingTop: "20px",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Medication Data Chart */}
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-center p-4 border-b border-cyan-500/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-500/10 rounded-lg">
                    <Pill className="text-cyan-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    Medication Data
                  </h2>
                </div>
              </div>

              {/* Tab buttons */}
              <div className="flex border-b border-slate-700/50 relative z-10">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMedicationViewType("count");
                  }}
                  className={`flex-1 py-2 text-center transition-colors ${
                    medicationViewType === "count"
                      ? "text-cyan-400 border-b-2 border-cyan-400"
                      : "text-slate-400 hover:text-slate-300"
                  }`}
                >
                  Count
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMedicationViewType("type");
                  }}
                  className={`flex-1 py-2 text-center transition-colors ${
                    medicationViewType === "type"
                      ? "text-cyan-400 border-b-2 border-cyan-400"
                      : "text-slate-400 hover:text-slate-300"
                  }`}
                >
                  Type
                </button>
              </div>

              <div className="p-6">
                {medicationViewType === "count" ? (
                  <div
                    className="cursor-pointer"
                    onClick={() =>
                      openModalChart(
                        analyticsData.medicationData,
                        "bar",
                        "Medication Count"
                      )
                    }
                  >
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={analyticsData.medicationData}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="rgba(255,255,255,0.1)"
                        />
                        <XAxis
                          dataKey="name"
                          stroke="#94a3b8"
                          tick={{ fill: "#94a3b8" }}
                          tickFormatter={(value) => value.split(" ")[0]}
                        />
                        <YAxis stroke="#94a3b8" tick={{ fill: "#94a3b8" }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count">
                          {analyticsData.medicationData?.map((_, index) => (
                            <Cell key={index} fill={getRandomColor()} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div
                    className="cursor-pointer"
                    onClick={() =>
                      openModalChart(
                        analyticsData.medicationTypeData,
                        "pie",
                        "Medication Types"
                      )
                    }
                  >
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={analyticsData.medicationTypeData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label
                        >
                          {analyticsData.medicationTypeData?.map(
                            (entry, index) => (
                              <Cell key={index} fill={entry.fill} />
                            )
                          )}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            <div
              onClick={() =>
                openModalChart(
                  analyticsData.matReoccurrenceData,
                  "bar",
                  "MAT vs Reoccurrence"
                )
              }
              className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500 cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-center p-4 border-b border-cyan-500/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Pill className="text-purple-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    MAT vs Reoccurrence
                  </h2>
                </div>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={analyticsData.matReoccurrenceData}
                    margin={{ top: 20, right: 30, left: 15, bottom: 40 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.1)"
                    />
                    <XAxis
                      dataKey="name"
                      stroke="#94a3b8"
                      tick={{ fill: "#94a3b8" }}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      tick={{ fill: "#94a3b8" }}
                      tickFormatter={(value) => `${value}%`}
                      domain={[0, 100]}
                    />
                    <Tooltip content={<MATTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      wrapperStyle={{ paddingTop: "10px" }}
                    />
                    <Bar
                      name="Reoccurrence Rate"
                      dataKey="reoccurrenceValue"
                      fill="#FB7185"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      name="Sobriety Rate"
                      dataKey="soberValue"
                      fill="#4ADE80"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Substance of Choice Chart */}
            <div
              onClick={() =>
                openModalChart(
                  analyticsData.substanceOfChoiceData,
                  "pie",
                  "Substance of Choice"
                )
              }
              className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl
             overflow-visible hover:shadow-2xl transition-all duration-500
             cursor-pointer"
            >
              <div
                className="absolute inset-0 bg-gradient-to-b from-slate-800/50
                  to-transparent opacity-0 group-hover:opacity-100
                  transition-opacity duration-500"
              />
              <div
                className="relative flex items-center justify-center p-4
                  border-b border-cyan-500/30"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-pink-500/10 rounded-lg">
                    {/* use any Lucide icon you like */}
                    <Activity className="text-pink-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    Substance&nbsp;of&nbsp;Choice
                  </h2>
                </div>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={analyticsData.substanceOfChoiceData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label
                    >
                      {analyticsData.substanceOfChoiceData?.map(
                        (entry, idx) => (
                          <Cell key={idx} fill={entry.fill} />
                        )
                      )}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend verticalAlign="bottom" height={65} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            {/* Recidivism Chart */}
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-visible hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Header with filter dropdown and info icon */}
              <div className="relative p-4 border-b border-cyan-500/30">
                <div className="flex items-center justify-center mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500/10 rounded-lg">
                      <Scale className="text-red-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">
                      Recidivism Rate
                    </h2>
                    {/* Info Icon with Tooltip */}
                    <div className="relative">
                      <button
                        onMouseEnter={() => setShowRecidivismInfo(true)}
                        onMouseLeave={() => setShowRecidivismInfo(false)}
                        onClick={(e) => e.stopPropagation()}
                        className="p-1 text-slate-400 hover:text-cyan-400 transition-colors"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>

                      {/* Tooltip */}
                      {showRecidivismInfo && (
                        <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-80 bg-slate-800 border border-slate-600 rounded-lg p-3 text-sm text-slate-200 shadow-lg z-50">
                          <div className="space-y-2">
                            <p className="font-medium text-cyan-400">
                              Recidivism Estimates
                            </p>
                            <p>
                              These numbers are estimates based on available
                              alumni data. Not all former residents can be
                              tracked post-discharge.
                            </p>
                            <p>
                              Organizations may define recidivism differently
                              (re-arrest, re-incarceration, etc.) based on their
                              tracking methods and data sources.
                            </p>
                          </div>
                          {/* Arrow pointer */}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Filter Dropdown */}
                <div className="flex items-center justify-center">
                  <select
                    value={recidivismFilter}
                    onChange={(e) => {
                      e.stopPropagation();
                      setRecidivismFilter(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="px-3 py-1 bg-slate-800/80 border border-slate-700 rounded-lg text-white text-sm hover:border-slate-600 transition-colors cursor-pointer"
                  >
                    <option value="all">All Alumni</option>
                    <option value="probationParole">Probation/Parole</option>
                  </select>
                </div>
              </div>

              {/* Chart area - clickable */}
              <div
                className="p-6 cursor-pointer"
                onClick={() => {
                  const currentRecidivismData = processRecidivismData(
                    residents,
                    recidivismFilter
                  );
                  const title = `Recidivism Rate - ${
                    recidivismFilter === "all"
                      ? "All Alumni"
                      : "Probation/Parole"
                  }`;
                  openModalChart(currentRecidivismData, "pie", title);
                }}
              >
                {(() => {
                  const currentRecidivismData = processRecidivismData(
                    residents,
                    recidivismFilter
                  );

                  // Calculate recidivism percentage
                  const totalAlumni = currentRecidivismData.reduce(
                    (sum, item) => sum + item.value,
                    0
                  );
                  const rearrested = currentRecidivismData.find(
                    (item) => item.name === "Re-arrested"
                  );
                  const recidivismPercentage =
                    totalAlumni > 0 && rearrested
                      ? parseFloat(rearrested.percentage)
                      : 0;

                  return (
                    <>
                      <div className="flex flex-col items-center mb-4">
                        <span className="text-4xl font-bold text-red-400">
                          {recidivismPercentage}%
                        </span>
                        <span className="text-slate-400">
                          Recidivism Rate ({totalAlumni} alumni)
                        </span>
                      </div>

                      <ResponsiveContainer width="100%" height={230}>
                        <PieChart>
                          <Pie
                            data={currentRecidivismData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label
                          >
                            {currentRecidivismData.map((entry, index) => (
                              <Cell key={index} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            wrapperStyle={{
                              paddingTop: "20px",
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ModalChart
        isOpen={!!modalChartData}
        onClose={closeModalChart}
        chartData={modalChartData}
        chartType={modalChartType}
        title={modalTitle}
      />
      <RecoveryModal
        isOpen={!!houseModalData}
        onClose={() => setHouseModalData(null)}
        recoveryData={houseModalData}
        title={houseModalTitle}
      />
      {/* Calculation Modal */}
      <CalculationModal
        isOpen={calculationModalOpen}
        onClose={() => setCalculationModalOpen(false)}
        title={calculationTitle}
        content={calculationContent}
      />
    </>
  );
};

export default Analytics;
